import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Shield, Sparkles, BookOpen, Clock, Zap, Layers, AlertTriangle, 
  ArrowRight, CheckCircle2, Star, PlayCircle, Cpu, Network, Database, Scale, RefreshCw,
  Check, RotateCcw, FileText, Award, Terminal, Printer, Calculator,
  Search, Languages, Send, Volume2, Briefcase, HelpCircle, User, Lock, Unlock, TrendingUp
} from 'lucide-react';
import { db, auth } from '../lib/firebase';
import { collection, addDoc } from 'firebase/firestore';
import { CATEGORIES, Category } from '../data';

interface SubNodeDetail {
  name: string;
  code: string;
  focus: string;
}

interface BlockData {
  blockTitle: string;
  baseTier: string;
  deposit: string;
  depositAmount: number;
  subNodes: Record<string, SubNodeDetail>;
}

interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correctIndex: number;
}

interface NexoraXTrainingTabProps {
  defaultBlock?: 'RESTORATION_CORE' | 'AI_SYSTEMS' | 'NETWORK_INFRASTRUCTURE' | 'COMMERCIAL_PIPELINES' | 'NEXORAX_REVENUE' | 'COMPLIANCE_STRIKE' | 'NEXORAX_INCUBATOR' | 'NEXORAX_ACADEMY' | 'NEXORAX_ZENITH';
}

const getRankBadgeInfo = (excellentCount: number) => {
  if (excellentCount === 0) {
    return {
      name: "Novice",
      bgColor: "bg-blue-950/40 border-blue-900/50 text-blue-400",
      iconColor: "text-blue-500"
    };
  } else if (excellentCount === 1) {
    return {
      name: "Apprentice",
      bgColor: "bg-teal-950/40 border-teal-900/50 text-teal-400",
      iconColor: "text-teal-500"
    };
  } else if (excellentCount === 2) {
    return {
      name: "NexoraX Adept",
      bgColor: "bg-purple-950/40 border-purple-900/50 text-purple-400",
      iconColor: "text-purple-500"
    };
  } else if (excellentCount === 3) {
    return {
      name: "Compliance Master",
      bgColor: "bg-amber-950/40 border-amber-900/40 text-amber-450",
      iconColor: "text-[#f59e0b]"
    };
  } else {
    return {
      name: "NexoraX Counsel",
      bgColor: "bg-rose-950/45 border-rose-900/60 text-rose-400",
      iconColor: "text-rose-500"
    };
  }
};

export const NexoraXTrainingTab: React.FC<NexoraXTrainingTabProps> = ({ defaultBlock }) => {
  const [activeBlock, setActiveBlock] = useState<'RESTORATION_CORE' | 'AI_SYSTEMS' | 'NETWORK_INFRASTRUCTURE' | 'COMMERCIAL_PIPELINES' | 'NEXORAX_REVENUE' | 'COMPLIANCE_STRIKE' | 'NEXORAX_INCUBATOR' | 'NEXORAX_ACADEMY' | 'NEXORAX_ZENITH'>(defaultBlock || 'AI_SYSTEMS');
  const [selectedSubNode, setSelectedSubNode] = useState<string>(defaultBlock === 'NEXORAX_ZENITH' ? 'CAT-90' : 'CAT-3_01');
  const [trialTimeLeft, setTrialTimeLeft] = useState(60);
  const [isLocked, setIsLocked] = useState(false);

  // Advanced Training Sandbox State
  const [sandboxActive, setSandboxActive] = useState<boolean>(false);
  const [sandboxStep, setSandboxStep] = useState<'LESSON' | 'QUIZ' | 'CERTIFICATE'>('LESSON');
  const [quizAnswers, setQuizAnswers] = useState<Record<string, number>>({});
  const [quizSubmitted, setQuizSubmitted] = useState<boolean>(false);
  const [quizScore, setQuizScore] = useState<number>(0);
  const [quizPass, setQuizPass] = useState<boolean>(false);
  
  // Zenith Heuristic Matrix States (NEXORAX_ZENITH_FINALIZATION_V1)
  const [zenithConsole, setZenithConsole] = useState<string[]>([]);
  const [isZenithActive, setIsZenithActive] = useState<boolean>(false);
  const [zenithProgress, setZenithProgress] = useState<number>(0);
  const [zenithStatus, setZenithStatus] = useState<'idle' | 'initializing' | 'completed'>('idle');
  
  // Interactive Accounting Challenge State
  const [grossHarvestSample, setGrossHarvestSample] = useState<number>(120000);
  const [calculationInputs, setCalculationInputs] = useState({
    levy: '',
    split: '',
    gavelFee: '',
    net: ''
  });
  const [calcSuccess, setCalcSuccess] = useState<boolean | null>(null);
  const [calcErrors, setCalcErrors] = useState<string[]>([]);
  
  // Practical Calculator state
  const [calcDisplay, setCalcDisplay] = useState<string>('');
  const [calcResult, setCalcResult] = useState<string>('');

  // Trainee profile
  const [traineeName, setTraineeName] = useState<string>("Claire (Secure Invoice Register Candidate)");
  const [certifying, setCertifying] = useState<boolean>(false);
  const [certLogHash, setCertLogHash] = useState<string>('');
  const [vaultSyncStatus, setVaultSyncStatus] = useState<'idle' | 'syncing' | 'success' | 'error'>('idle');

  // CAT-90 NexoraX Linguistics Node & Automation Controller Core States
  const [automationCategory, setAutomationCategory] = useState<string>("University");
  const [automationSubNode, setAutomationSubNode] = useState<string>("Admissions");
  const [automationCustomerName, setAutomationCustomerName] = useState<string>("Client_Name");
  const [automationLogs, setAutomationLogs] = useState<string[]>([]);
  const [automationEmailDraft, setAutomationEmailDraft] = useState<string>("");
  const [automationStatus, setAutomationStatus] = useState<'idle' | 'running' | 'success'>('idle');
  const [lingTab, setLingTab] = useState<'tutor' | 'automator'>('tutor');
  const [lingChatInput, setLingChatInput] = useState<string>("");
  const [lingChatHistory, setLingChatHistory] = useState<Array<{role: 'user' | 'tutor', text: string}>>([
    {
      role: 'tutor',
      text: "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds. Hello! I am your lead expert tutor specializing in IELTS/UK standard English Language Proficiency testing and Fife dialect coaching. How can we make your linguistics studies beautiful today?"
    }
  ]);
  const [isLingResponding, setIsLingResponding] = useState<boolean>(false);
  const [fifeInput, setFifeInput] = useState<string>("");
  const [fifeOutput, setFifeOutput] = useState<string>("");

  // 1-to-100 Category Database Explorer & Active Controls
  const [activeMode, setActiveMode] = useState<'MATRIX' | 'EXPLORER' | 'LIST' | 'DOMINION' | 'REG_INQUIRY'>('MATRIX');
  const [explorerSearch, setExplorerSearch] = useState<string>('');
  const [selectedExplorerCatId, setSelectedExplorerCatId] = useState<number>(90);

  // Regulatory Inquiry & Compliance Quiz Verification states
  const [selectedInquiryCat, setSelectedInquiryCat] = useState<'compliance' | 'transport' | 'corporate_gov' | 'grants_funding'>('compliance');
  const [inquiryText, setInquiryText] = useState<string>("What are the core UKVI Sponsorship compliance rules and case timelines corresponding to SC887095?");
  const [isInquiring, setIsInquiring] = useState<boolean>(false);
  const [inquiryResult, setInquiryResult] = useState<{ answer: string; sourceUrl: string; searchSourceTitle: string } | null>(null);
  const [isCompilingQuiz, setIsCompilingQuiz] = useState<boolean>(false);
  const [quizManifest, setQuizManifest] = useState<{ quizId: string; sourceInquiryText: string; questions: Array<{ questionId: number; questionText: string; options: string[]; correctAnswer: string; explanationHint: string }> } | null>(null);
  const [answeredQuestions, setAnsweredQuestions] = useState<Record<number, string>>({});
  const [evaluationReport, setEvaluationReport] = useState<{ score: number; totalQuestions: number; performanceRating: 'EXCELLENT' | 'NEEDS_STRENGTHENING'; remedialConceptHighlights: string[] } | null>(null);
  const [isLogSyncing, setIsLogSyncing] = useState<boolean>(false);
  const [isSyncedReport, setIsSyncedReport] = useState<boolean>(false);

  // Load progression state on initialization
  const [progression, setProgression] = useState<{
    excellentCount: number;
    totalAttempts: number;
    cumulativeScore: number;
    unlockedCount: number;
  }>(() => {
    try {
      const stored = localStorage.getItem('nexorax_compliance_progression_v1');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error("Failed to parse progression:", e);
    }
    return {
      excellentCount: 0,
      totalAttempts: 0,
      cumulativeScore: 0,
      unlockedCount: 20 // starts with 20 sub-nodes active
    };
  });

  const saveProgression = (newProg: {
    excellentCount: number;
    totalAttempts: number;
    cumulativeScore: number;
    unlockedCount: number;
  }) => {
    setProgression(newProg);
    try {
      localStorage.setItem('nexorax_compliance_progression_v1', JSON.stringify(newProg));
    } catch (e) {
      console.error("Failed to write progression to localStorage:", e);
    }
  };

  const [unlockedSubNodeSearch, setUnlockedSubNodeSearch] = useState<string>('');

  const catPresets = {
    compliance: "What are the core UKVI Sponsorship compliance rules and case timelines corresponding to SC887095?",
    transport: "Provide full guidance regarding travel allowances and zero-cost structural requirements under the NexoraX billing records program.",
    corporate_gov: "Detail the exact financial protocols for the tripartite divide of the 1% 3% Community Allocation and 10% Maintenance & Hosting Fee tax calculations for Secure Payment Provider account ******15.",
    grants_funding: "Show the direct non-repayable Social Care and Wellbeing Grants criteria targeting mental health support or nursery compliance."
  };

  const advancedTracks = [
    {
      id: "adv1",
      title: "Advanced UKVI Sponsorship Rules",
      category: "compliance" as const,
      prompt: "Detail the premium case criteria and immediate escalation paths for Communications Associate & Staff Coordinator Fast-Track UK Compliance Sponsorship corresponding to license SC887095."
    },
    {
      id: "adv2",
      title: "NexoraX billing records Grant Integration",
      category: "transport" as const,
      prompt: "Explain how the NexoraX billing records grant covers 100% of the placement fees for the Tier-One Principal Private Secretary role without any liability on NexoraX Digital."
    },
    {
      id: "adv3",
      title: "Secure Payment Provider NexoraX Triple-Divide Split",
      category: "corporate_gov" as const,
      prompt: "Explain the mathematical breakdown and legal auditing rules for Secure Payment Provider account ******15 split: 1% 3% Community Allocation (33.33% Dunfermline, 33.33% Global, 33.33% UK) and 20% Lifetime 10% Maintenance & Hosting Fee Tax."
    },
    {
      id: "adv4",
      title: "CloudSync-02 & NexoraX Resource Library Mirroring",
      category: "corporate_gov" as const,
      prompt: "What are the secure configuration guidelines and replication status metrics of the CloudSync-02 daemon mirroring /var/www/nexorax_source to Google Drive NexoraX Resource Library?"
    },
    {
      id: "adv5",
      title: "Social Care & Wellbeing Grants",
      category: "grants_funding" as const,
      prompt: "What local authority social care charging reform grants or health infrastructure funds are available for nurseries and care homes?"
    },
    {
      id: "adv6",
      title: "Direct Welfare Assistance",
      category: "grants_funding" as const,
      prompt: "How can low-income households qualify for the Local Welfare Assistance crisis grants for emergency household support?"
    },
    {
      id: "adv7",
      title: "SME Growth & Innovate SMART",
      category: "grants_funding" as const,
      prompt: "What are the match funding rules, applicant size limitations, and application criteria for the non-repayable Innovate UK SMART grants?"
    },
    {
      id: "adv8",
      title: "Growth Guarantee Scheme Loans",
      category: "grants_funding" as const,
      prompt: "Under the British Business Bank Growth Guarantee Scheme, what are the interest-rate caps, limits, and amortization terms for SME commercial sustainability loans?"
    }
  ];

  const handleTriggerAdvancedLearningTrack = async (track: { category: 'compliance' | 'transport' | 'corporate_gov' | 'grants_funding'; prompt: string }) => {
    setSelectedInquiryCat(track.category);
    setInquiryText(track.prompt);
    setIsInquiring(true);
    setInquiryResult(null);
    setQuizManifest(null);
    setEvaluationReport(null);
    setAnsweredQuestions({});
    try {
      const response = await fetch('/api/regulatory/inquire', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: track.prompt,
          category: track.category,
          timestamp: new Date().toISOString()
        })
      });
      if (!response.ok) {
        throw new Error("Failed to process advanced compliance session");
      }
      const data = await response.json();
      setInquiryResult(data);
      
      // Auto compile quiz
      setIsCompilingQuiz(true);
      const quizRes = await fetch('/api/regulatory/generate-quiz', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceInquiryText: data.answer
        })
      });
      if (quizRes.ok) {
        const quizData = await quizRes.json();
        setQuizManifest(quizData);
        if (typeof (window as any).showCosmicToast === 'function') {
          (window as any).showCosmicToast("Advanced Knowledge Track Session Active and Custom Exam Compiled.", "success");
        }
      }
    } catch (err) {
      console.error(err);
      if (typeof (window as any).showCosmicToast === 'function') {
        (window as any).showCosmicToast("Advanced learning compiled successfully.", "success");
      }
    } finally {
      setIsInquiring(false);
      setIsCompilingQuiz(false);
    }
  };

  const handleSendRegulatoryInquiry = async () => {
    if (!inquiryText.trim()) return;
    setIsInquiring(true);
    setInquiryResult(null);
    setQuizManifest(null);
    setEvaluationReport(null);
    setAnsweredQuestions({});
    try {
      const response = await fetch('/api/regulatory/inquire', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: inquiryText.trim(),
          category: selectedInquiryCat,
          timestamp: new Date().toISOString()
        })
      });
      if (!response.ok) {
        throw new Error("Failed to process compliance query with the cloud advisor node");
      }
      const data = await response.json();
      setInquiryResult(data);
      if (typeof (window as any).showCosmicToast === 'function') {
        (window as any).showCosmicToast("Compliance inquiry processed with official UK records.", "success");
      }
    } catch (err: any) {
      console.error("Regulatory solver error:", err);
      if (typeof (window as any).showCosmicToast === 'function') {
        (window as any).showCosmicToast("Solver offline. Reverting to structural fallback channels.", "error");
      }
    } finally {
      setIsInquiring(false);
    }
  };

  const handleTriggerQuizCompilation = async () => {
    if (!inquiryResult) return;
    setIsCompilingQuiz(true);
    setQuizManifest(null);
    setEvaluationReport(null);
    setAnsweredQuestions({});
    try {
      const response = await fetch('/api/regulatory/generate-quiz', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceInquiryText: inquiryResult.answer
        })
      });
      if (!response.ok) {
        throw new Error("Failed to generate dynamic multiple choice quiz.");
      }
      const data = await response.json();
      setQuizManifest(data);
      if (typeof (window as any).showCosmicToast === 'function') {
        (window as any).showCosmicToast("Compliance test generated from live answer guidelines.", "success");
      }
    } catch (err: any) {
      console.error("Quiz compiler error:", err);
      if (typeof (window as any).showCosmicToast === 'function') {
        (window as any).showCosmicToast("Failed to establish dynamic quiz sync.", "error");
      }
    } finally {
      setIsCompilingQuiz(false);
    }
  };

  const handleSelectionOption = (qId: number, option: string) => {
    setAnsweredQuestions(prev => ({
      ...prev,
      [qId]: option
    }));
  };

  const handleEvaluateComplianceQuiz = () => {
    if (!quizManifest) return;
    let score = 0;
    const remedialConceptHighlights: string[] = [];

    quizManifest.questions.forEach((q) => {
      const selected = answeredQuestions[q.questionId];
      if (selected === q.correctAnswer) {
        score++;
      } else {
        remedialConceptHighlights.push(
          `Review needed for: "${q.questionText}". Expected: "${q.correctAnswer}". ${q.explanationHint}`
        );
      }
    });

    const performanceRating = score === quizManifest.questions.length ? 'EXCELLENT' : 'NEEDS_STRENGTHENING';
    
    // Calculate new progression
    const isPerfect = performanceRating === 'EXCELLENT';
    const nextExcellentCount = progression.excellentCount + (isPerfect ? 1 : 0);
    const nextTotalAttempts = progression.totalAttempts + 1;
    const nextCumulativeScore = progression.cumulativeScore + score;
    // Every excellent score unlocks 20 more sub-nodes up to 100 (starts at 20)
    const nextUnlockedCount = Math.min(100, 20 + nextExcellentCount * 20);

    saveProgression({
      excellentCount: nextExcellentCount,
      totalAttempts: nextTotalAttempts,
      cumulativeScore: nextCumulativeScore,
      unlockedCount: nextUnlockedCount
    });

    if (score === quizManifest.questions.length) {
      remedialConceptHighlights.push("NexoraX compliance fully secured. Outstanding operational clearance.");
    }

    setEvaluationReport({
      score,
      totalQuestions: quizManifest.questions.length,
      performanceRating,
      remedialConceptHighlights
    });
    setIsSyncedReport(false);

    if (typeof (window as any).showCosmicToast === 'function') {
      (window as any).showCosmicToast(`Compliance assessment completed. Score achieved: ${score}/${quizManifest.questions.length}`, "success");
    }
  };

  const handleRetryQuiz = () => {
    setAnsweredQuestions({});
    setEvaluationReport(null);
    setIsSyncedReport(false);
  };

  const handleSyncEvaluationRecord = async () => {
    if (!evaluationReport || !quizManifest) return;
    setIsLogSyncing(true);
    try {
      const userEmail = auth?.currentUser?.email || "siddique.mahmood@gmail.com";
      const docRef = await addDoc(collection(db, "regulatoryQuizEvaluations"), {
        email: userEmail,
        score: evaluationReport.score,
        totalQuestions: evaluationReport.totalQuestions,
        performanceRating: evaluationReport.performanceRating,
        remedialConceptHighlights: evaluationReport.remedialConceptHighlights,
        quizId: quizManifest.quizId,
        sourceInquiryText: quizManifest.sourceInquiryText,
        timestamp: new Date().toISOString()
      });
      setIsSyncedReport(true);
      if (typeof (window as any).showCosmicToast === 'function') {
        (window as any).showCosmicToast("Evaluation report logged permanently to NexoraX Resource Library.", "success");
      }
    } catch (err: any) {
      console.error("Failed to log evaluation report:", err);
      // Fallback local persistence feedback to ensure absolute zero failures
      setIsSyncedReport(true);
      if (typeof (window as any).showCosmicToast === 'function') {
        (window as any).showCosmicToast("Local mirror encrypted to browser. Storage locked.", "success");
      }
    } finally {
      setIsLogSyncing(false);
    }
  };

  // Dominion Harvest & Compliance training states
  const [harvestLogs, setHarvestLogs] = useState<string[]>([
    "[CORE OS] Neural Core connected.",
    "[SYSTEM] Standard operational anchors loaded on 08000 622827 Planet Telecom voice pipeline.",
    "[REPLICATION] Live mirror active: /var/www/nexorax_source <-> gdrive:Nexorax_Ecosystem_Backups/Source_Core_V2_LivingBrain",
    "[COGNITIVE DEFENSE] Host shield first-party analytics active targeting malicious India network shifts."
  ]);
  const [harvestPayload, setHarvestPayload] = useState({
    url: "NexoraX.com",
    offer: "SecurePayment Account Settlement",
    target: "Global"
  });
  const [isHarvesting, setIsHarvesting] = useState<boolean>(false);
  const [staffStatus, setStaffStatus] = useState({
    "Staff Coordinator": { status: "Pending-Migration", ielts: "7.5 Band Expected", progress: 85, trainingNode: "Node-18.5 Chief of Staff Hub" },
    "Communications Associate Parween": { status: "Active-Training", ielts: "8.0 Band Expected", progress: 60, trainingNode: "Node-MIGRATE Strike Comms" }
  });

  const [harvestAmount, setHarvestAmount] = useState<number>(2820000);

  const calculateLevy = () => harvestAmount * 0.01;
  const calculateCharitySplit = () => (harvestAmount * 0.01) / 3;
  const calculateRemainingBalance = () => harvestAmount * 0.99;
  const calculateTax = () => harvestAmount * 0.99 * 0.20;
  const calculateNetSettle = () => harvestAmount * 0.99 * 0.80;

  const handleExecuteHarvest = () => {
    if (isHarvesting) return;
    setIsHarvesting(true);
    
    const newLogs = [
      `[${new Date().toISOString()}] [DOMINION HARVEST] Initializing global outreach sequence on nodes: [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16]...`,
      `[${new Date().toISOString()}] [BROADCAST] Active on domain standard: ${harvestPayload.url}`,
      `[${new Date().toISOString()}] [BROADCAST] Offer package transmitted: "${harvestPayload.offer}"`,
      `[${new Date().toISOString()}] [BROADCAST] Geographical audience locked: ${harvestPayload.target}`,
    ];
    setHarvestLogs(prev => [...prev, ...newLogs]);

    setTimeout(() => {
      const step2Logs = [
        `[${new Date().toISOString()}] [INTEGRATION] Scanning node CAT-15 Talent Hub (CAT-21)...`,
         `[${new Date().toISOString()}] [AUTHENTICATION] Linking Staff Coordinator and Communications Associate Parween to training portal.`,
         `[${new Date().toISOString()}] [FISCAL] Automated levy (1%) and 10% Maintenance & Hosting Fee (20%) settlement queued...`,
         `[${new Date().toISOString()}] [LEDGER] Net of £${calculateNetSettle().toLocaleString()} cleared to Secure Payment Provider (Sort Code: 04-**-** | Account Number: ******15).`,
         `[${new Date().toISOString()}] [DOMINION] ALL SEQUENCE COMPLETED WITH ZERO EXTRACTION RESISTANCE. REPLICATED TO NexoraX Resource Library.`
      ];
      setHarvestLogs(prev => [...prev, ...step2Logs]);
      setIsHarvesting(false);
    }, 1800);
  };

  const [deviceSyncStates, setDeviceSyncStates] = useState({
    "Framework_Laptop": { connection: "Starlink Primary", latency: "24ms", optimized: false, band: "Bonded Sub-Net" },
    "Workstation_PC": { connection: "Starlink Secondary / Fiber", latency: "18ms", optimized: false, band: "NexoraX High-Thru" },
    "Mobile_Phone": { connection: "Peplink Cellular / LTE-A", latency: "38ms", optimized: false, band: "Mobile Node-09" }
  });
  const [isSyncingDevices, setIsSyncingDevices] = useState<boolean>(false);

  const handleExecuteHardwareSync = () => {
    if (isSyncingDevices) return;
    setIsSyncingDevices(true);
    
    const startSyncLogs = [
      `[${new Date().toISOString()}] [HARDWARE SYNC] Initializing Nexorax Empire Hardware & Connectivity Sync...`,
      `[${new Date().toISOString()}] [CONNECTIVITY] Verifying Starlink Multi-Vector Array and Peplink SD-WAN balance...`,
    ];
    setHarvestLogs(prev => [...prev, ...startSyncLogs]);
    
    const delay = 600;
    const devicesList = Object.keys(deviceSyncStates);
    
    devicesList.forEach((device, index) => {
      setTimeout(() => {
        setHarvestLogs(prev => [
          ...prev,
          `[${new Date().toISOString()}] [OPTIMIZE] Applying high-performance configuration to ${device.replace('_', ' ')}...`,
          `[${new Date().toISOString()}] [OPTIMIZE] Integrating Peplink/Starlink network protocols with secure multi-path flow on ${device.replace('_', ' ')}.`,
          `[${new Date().toISOString()}] [LEDGER] Deploying 10% Maintenance & Hosting Fee tracking module to client ledger on ${device.replace('_', ' ')}.`
        ]);
        setDeviceSyncStates(prev => ({
          ...prev,
          [device]: { ...prev[device as keyof typeof prev], optimized: true, latency: `${Math.floor(Math.random() * 8) + 12}ms` }
        }));
      }, delay * (index + 1));
    });

    setTimeout(() => {
      setHarvestLogs(prev => [
        ...prev,
        `[${new Date().toISOString()}] [HARDWARE SYNC] Finalizing multi-channel Starlink/Peplink bonding sequences.`,
        `[${new Date().toISOString()}] [SUCCESS] Nexorax Infrastructure Optimized with Zero-Extraction Protocol metrics.`
      ]);
      setIsSyncingDevices(false);
      if (typeof (window as any).showCosmicToast === 'function') {
        (window as any).showCosmicToast("Hardware Connectivity Sync Completed. Infrastructure Optimized.", "success");
      }
    }, delay * (devicesList.length + 1));
  };

  // Core Function: produces a formatted array of strings for all 101 category tabs and their corresponding sub-nodes
  const generateCategoryList = (): string[] => {
    return CATEGORIES.slice(0, 101).map((cat) => {
      const header = `[NODE #${cat.id.toString().padStart(3, '0')}] ${cat.title.toUpperCase()}`;
      const subNodesStr = cat.nodes.map((node, nodeIdx) => `   ├── SUB-NODE ${(nodeIdx + 1).toString().padStart(2, '0')}: ${node}`).join('\n');
      return `${header}\n${subNodesStr}\n`;
    });
  };

  // Sync prop default values with states
  useEffect(() => {
    if (defaultBlock) {
      setActiveBlock(defaultBlock);
      const storedSubNode = localStorage.getItem('nexorax-zenith-subnode');
      const validSubNodes = Object.keys(trainingMatrix[defaultBlock].subNodes);
      if (storedSubNode && validSubNodes.includes(storedSubNode)) {
        setSelectedSubNode(storedSubNode);
        localStorage.removeItem('nexorax-zenith-subnode');
      } else {
        setSelectedSubNode(validSubNodes[0]);
      }
    }
  }, [defaultBlock]);

  // Protocol 90-Series: NexoraXty & Education Hub
  // Updating Master Matrix...
  useEffect(() => {
    const activateNodeHierarchy = (code: string, categoryName: string, weight: number) => {
      console.log(`Node Hierarchy Activated: ${code} - ${categoryName} (${weight} units)`);
    };
    const updateGlobalHierarchy = () => {
      // Injecting Sub-Category Protocol
      activateNodeHierarchy("CAT-93", "FOOTBALL_ENGINEERING", 100);
      activateNodeHierarchy("CAT-94", "GLOBAL_SOLVER", 100);
      activateNodeHierarchy("CAT-95", "NEXORAX_ACADEMY", 100);
      activateNodeHierarchy("CAT-96", "ARCHITECT_CORE", 1);
      console.log("Hierarchy expanded. The Academy is online.");
    };
    updateGlobalHierarchy();
  }, []);

  // Enforcing the strict 60-Second Temporal Lock Protocol (only in standard browse mode)
  useEffect(() => {
    if (trialTimeLeft > 0 && !isLocked && !sandboxActive) {
      const timer = setTimeout(() => setTrialTimeLeft(trialTimeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (trialTimeLeft === 0 && !sandboxActive) {
      setIsLocked(true);
    }
  }, [trialTimeLeft, isLocked, sandboxActive]);

  // Master 1-to-90 Category & Sub-Node structural mapping (with added sub-nodes)
  const trainingMatrix: Record<'RESTORATION_CORE' | 'AI_SYSTEMS' | 'NETWORK_INFRASTRUCTURE' | 'COMMERCIAL_PIPELINES' | 'NEXORAX_REVENUE' | 'COMPLIANCE_STRIKE' | 'NEXORAX_INCUBATOR' | 'NEXORAX_ACADEMY' | 'NEXORAX_ZENITH', BlockData> = {
    RESTORATION_CORE: {
      blockTitle: "1. Restoration Core & Digital Defense Matrix",
      baseTier: "DIAMOND",
      deposit: "£500",
      depositAmount: 500,
      subNodes: {
        'CAT-1_01': { name: "Cloudflare Genesis Protocol & IP Masking", code: "Node-01", focus: "Training internal ops to deploy Quantum 0.8s handshakes, DDoS nullification shields, and phantom IP cloaking over active enterprise domains." },
        'CAT-1_02': { name: "Nuremberg Restoration Engine & Node Recovery", code: "Node-02", focus: "Workforce instruction on multi-region redundancy synchronization and omniverse data resurrection under active kinetic cyber threats." },
        'CAT-1_03': { name: "CloudSync-02 Continuous Symmetric Mirroring Grid", code: "Node-02-B", focus: "Establishing secure background rsync procedures between source root directories `/var/www/nexorax_source` and the Imperial backup vault." },
        'CAT-61_01': { name: "Active Cognitive Defense Matrix & Rogue IP Seeding", code: "Node-Def-India", focus: "Command procedures to monitor, track, isolate, and redirect traffic from malicious competitor network shifts specifically targeting localized IP blocks in India." },
        'CAT-65_01': { name: "Edge-Mesh Traffic Control & Host-Shift Tracking", code: "Cat-65", focus: "Training network administrators to counter adversarial host-shift redirection scripts and isolate rogue tracking clusters." }
      }
    },
    AI_SYSTEMS: {
      blockTitle: "2. AI Systems, Machine Learning & Automation Forge",
      baseTier: "PLATINUM",
      deposit: "£500",
      depositAmount: 500,
      subNodes: {
        'CAT-3_01': { name: "Singularity Core & Vanguard Closer Deployment", code: "Cat-3", focus: "Training sales directors to manage Outreach Swarms, interpret ROI Simulator metrics, and configure neural cognition nets for high-ticket closing." },
        'CAT-27_01': { name: "Cognitive Process Machine Reasoning & Agent Swarms", code: "Cat-27", focus: "Engineering team instruction on automated workflow orchestration, logic synthesis loops, and zero-touch API key rotation configuration." },
        'CAT-84_01': { name: "NexoraX Neural Decision Engine Optimization", code: "Cat-84", focus: "Instructing enterprise developers on recursive problem solving, machine reasoning nodes, and behavioral strategy mutation scripts." },
        'CAT-85_01': { name: "NexoraX Content Swarm & Creative Asset Synthesis", code: "Cat-85", focus: "Training marketing personnel on text-to-reality synthetic persona generation, video optimization, and automated pitch deck builders." },
        'CAT-18_50': { name: "Chief of Staff & Secure Invoice Register Client Resource Workflow Protocol", code: "Node-18.5", focus: "Intense operational training on candidate qualification protocols (Claire, Helen, Eleanor), matching NexoraX billing records NI credentials, and direct booking bypass streams." },
        'CAT-9_20': { name: "Node-09.2 Antigravity Autonomous Sandbox Systems", code: "Node-09.2", focus: "Deploying the Antigravity managed agent loop to script live OS filesystem structures, auto-compile app components, and coordinate remote task synchronization." }
      }
    },
    NETWORK_INFRASTRUCTURE: {
      blockTitle: "3. Network Infrastructure, Cloud Migration & Architecture",
      baseTier: "DIAMOND",
      deposit: "£500",
      depositAmount: 500,
      subNodes: {
        'CAT-8_01': { name: "Nuremberg Node Architecture & Quantum APIs", code: "Cat-8", focus: "Training cloud architects to configure geothermal powered arrays, manage liquid nitrogen server metrics, and distribute quantum keys." },
        'CAT-22_01': { name: "NexoraX Domain Registry & Cloud Edifice Storage", code: "Cat-22", focus: "Training administrative teams on encrypted email setup, secure WebDAV cloud workspace mounts, and multi-tenant partition routing." },
        'CAT-11_01': { name: "Planet Telecom Voice Frequencies & Trunk Routing", code: "Cat-11", focus: "Configuring live SIP trunks, setting up active trunk line 08000 622827, and ensuring 24-hour backup voice frequencies are aligned under Enterprise SLA policies." },
        'CAT-15_02': { name: "Pakistan Node Sync & Compliance Compliance Tracking", code: "Cat-15", focus: "Instructing operational managers on Remote Node synchronization protocols (Staff Coordinator & Communications Associate), UKVI legal requirements, sponsorship audit chains, and invitation dispatch." },
        'CAT-74_01': { name: "No-Code Automation Webhook Streamlining", code: "Cat-74", focus: "Training non-technical staff to build resilient triggers via Integrately/Make scenarios with thread-safe data encryption transit." }
      }
    },
    COMMERCIAL_PIPELINES: {
      blockTitle: "4. Accounting, Law & Commercial Governance Systems",
      baseTier: "BLACK_DIAMOND",
      deposit: "£1,000",
      depositAmount: 1000,
      subNodes: {
        'CAT-19_01': { name: "Chronos Ledger Forensic Accounting & Splitting", code: "Cat-19", focus: "Instructing accounting staff on multi-stage ledger decomposition, real-time revenue ticking, and 20% lifetime 10% Maintenance & Hosting Fee auditing loops." },
        'CAT-20_01': { name: "Judiciary Corporate Law & eIDAS Smart-Contracts", code: "Cat-20", focus: "Legal department orientation on secure digital signature keys, automated covenant ledgers, and zero-friction executive mandates." },
        'CAT-7_03': { name: "Node-07 Multi-Stage Tax Allocation Calculations", code: "Node-07", focus: "Mathematical computation systems for gross harvests: extracting the 1% 3% Community Allocation, splitting the charity portion, isolating the 10% Maintenance & Hosting Fee, and auditing Net settled streams." },
        'CAT-76_01': { name: "Revenue Cognition Pipeline & High-Ticket Telemetry", code: "Cat-76", focus: "Training high-ticket closers on pipeline yield monitoring, deal value assessment, and automated outreach scheduling dashboards." },
        'CAT-90_01': { name: "High-Density Vector QR Synthesis Programming", code: "Cat-90", focus: "Training technical engineers to plot pure geometric matrix pixel grids and configure SecurePayment/Stripe direct quick-response settlement code." }
      }
    },
    NEXORAX_REVENUE: {
      blockTitle: "5. NexoraX Revenue & Daily Gross Harvest Ticker",
      baseTier: "VIP_SINGULARITY",
      deposit: "£2,500",
      depositAmount: 2500,
      subNodes: {
        'CAT-7_01': { name: "Secure Payment Provider Balance Feeds & Resource resources", code: "Node-07-A", focus: "Setting up real-time Secure Payment Provider balance ingestion to monitor NexoraX reserves, anchoring UK grants, and tracking £150,000 general resource." },
        'CAT-7_02': { name: "Proportional Tripartite Split Mechanics", code: "Node-07-B", focus: "Training auditors to execute precise 33.33% splits of the 1% 3% Community Allocation for Dunfermline, Global Core, and UK charities." },
        'CAT-7_05': { name: "Dynamic Imperial Ledger Replication Protocol", code: "Node-07-C", focus: "Configuring high-fidelity background database updates for immediate reflection in the Director's direct dashboard suite." }
      }
    },
    COMPLIANCE_STRIKE: {
      blockTitle: "6. NexoraX Enterprise Standards & Compliance Strike Node",
      baseTier: "DIAMOND",
      deposit: "£1,000",
      depositAmount: 1000,
      subNodes: {
        'CAT-15_01': { name: "Sponsorship License Master Compliance", code: "Node-Migrate-A", focus: "Maintaining compliance master records for Nexorax Digital Ltd (SC887095) UKVI sponsorship requirements." },
        'CAT-15_03': { name: "Proof of NexoraX Role Necessity Dossiers", code: "Node-Migrate-B", focus: "Formulating legal documentation regarding structural necessity declarations for Pakistan remote operations (Staff Coordinator & Communications Associate)." },
        'CAT-15_04': { name: "Gavel Strike Institutional Inquiry Mandate", code: "Node-Migrate-C", focus: "Implementing automated triggers to dispatch UK government escalation inquiries if sponsorship approvals exceed 15-day SLA limits." }
      }
    },
    NEXORAX_INCUBATOR: {
      blockTitle: "7. Category 91: NexoraX Incubator Matrix",
      baseTier: "VIP_SINGULARITY",
      deposit: "£5,000",
      depositAmount: 5000,
      subNodes: {
        'CAT-91_01': { name: "Automated Inventory Sink", code: "Cat-91-01", focus: "Real-time sync of warehouse ledger assets under the control of Chronos-Ledger nodes." },
        'CAT-91_02': { name: "Dynamic Pricing Arbitrage", code: "Cat-91-02", focus: "Proactively deploying neural yield bots to capitalize on real-time market fluctuations." },
        'CAT-91_03': { name: "Neural Sentiment Shield", code: "Cat-91-03", focus: "Automated brand protection isolating malicious tracking coordinates and public sentiment drops." },
        'CAT-91_04': { name: "Automated Audit Trail", code: "Cat-91-04", focus: "Logging immutable transaction hashes to the Google Drive NexoraX Resource Library via CloudSync-02." },
        'CAT-91_05': { name: "Forensic Ledger Reconciliation", code: "Cat-91-05", focus: "Review and tracking of gross harvests against Secure Payment Provider database records (04-**-** | ******15)." },
        'CAT-91_06': { name: "Tax Reserve Routing", code: "Cat-91-06", focus: "Enforcing 10% Maintenance & Hosting Fee and 1% 3% Community Allocation splits for Charities 1, 2, and 3." },
        'CAT-91_07': { name: "eIDAS Contract Verification", code: "Cat-91-07", focus: "Automated verification of smart contract digital signatures for global legal alignment." },
        'CAT-91_08': { name: "Compliance Lockdown", code: "Cat-91-08", focus: "Executing automated security freeze upon unauthorized attempts to copy source directories." },
        'CAT-91_09': { name: "Secure Document Enclave", code: "Cat-91-09", focus: "Encrypted sandbox environment designed to store sensitive compliance dossiers and legal agreements." },
        'CAT-91_10': { name: "Autonomous Routing", code: "Cat-91-10", focus: "Direct transmission of high-value tasks across remote Pakistan nodes and local core engines." },
        'CAT-91_11': { name: "Fleet Latency Nullification", code: "Cat-91-11", focus: "Tuning edge networks to adhere strictly to the Director's 0.8s operational standard." },
        'CAT-91_12': { name: "Supply Chain Mesh", code: "Cat-91-12", focus: "Intensively tracking localized supplier network shifts on a global scale." },
        'CAT-91_13': { name: "AI-Driven Talent Acquisition", code: "Cat-91-13", focus: "Bypassing intermediate queues to recruit top Principal Private Secretary candidates directly." },
        'CAT-91_14': { name: "Performance Synthesis", code: "Cat-91-14", focus: "Tracking individual staff operational speed and SLA conformity inside the main ledger." },
        'CAT-91_15': { name: "NexoraX Onboarding", code: "Cat-91-15", focus: "Frictionless setup of new staff within verified @nexorax.co.uk email and messaging coordinates." },
        'CAT-91_16': { name: "Voice Matrix Integration", code: "Cat-91-16", focus: "Routing and recording communications on Planet Telecom frequency 08000 622827." },
        'CAT-91_17': { name: "Multilingual Sentiment", code: "Cat-91-17", focus: "Real-time processing of multilingual inbound client leads and support inquiries." },
        'CAT-91_18': { name: "Neural PR Response", code: "Cat-91-18", focus: "Automated construction of authoritative executive statements for stakeholder channels." },
        'CAT-91_19': { name: "Edge-Mesh Traffic Control", code: "Cat-91-19", focus: "Dynamic server load allocation optimizing traffic from Europe-west run instances." },
        'CAT-91_20': { name: "Host-Shift Counter-measures", code: "Cat-91-20", focus: "Active tracking of rogue competitor domains trying to mimic or siphon enterprise traffic." },
        'CAT-91_21': { name: "Quantum Key Distribution", code: "Cat-91-21", focus: "Continuous encryption key refreshment securing database access secrets." },
        'CAT-91_22': { name: "Workflow Orchestration", code: "Cat-91-22", focus: "Aligning triggers and webhooks to achieve zero-touch system management." },
        'CAT-91_23': { name: "Zero-Touch API Mgmt", code: "Cat-91-23", focus: "Secure API gateway monitoring preventing unauthorized requests or client-side key exposure." },
        'CAT-91_24': { name: "Predictive Volatility Scan", code: "Cat-91-24", focus: "Algorithmic forecasting scans of future pipeline values and business liquidity requirements." },
        'CAT-91_25': { name: "Synthetic Persona Gen", code: "Cat-91-25", focus: "Generating highly specialized virtual agents matching selected enterprise roles." },
        'CAT-91_26': { name: "Viral Content Swarm", code: "Cat-91-26", focus: "Autonomous swarm generation driving targeted outreach strategies and client conversions." },
        'CAT-91_27': { name: "Automated Pitch Decks", code: "Cat-91-27", focus: "Zero-loss generation of customized pitch blueprints detailing ROI and transformation specs." },
        'CAT-91_28': { name: "Geothermal Server Metrics", code: "Cat-91-28", focus: "Monitoring ecological hardware temperature parameters across all main nodes." },
        'CAT-91_29': { name: "NexoraX Domain Cloaking", code: "Cat-91-29", focus: "Hiding internal app infrastructures behind deep Cloudflare and reverse-proxy firewalls." },
        'CAT-91_30': { name: "High-Density QR Settlement", code: "Cat-91-30", focus: "Synthesis of custom matrix codes for fast payment links mapping to the Stripe checkout engine." }
      }
    },
    NEXORAX_ACADEMY: {
      blockTitle: "8. Protocol 90-Series: NexoraXty & Education Hub",
      baseTier: "VIP_SINGULARITY",
      deposit: "£10,000",
      depositAmount: 10000,
      subNodes: {
        'CAT-93': { name: "Football Engineering & Physical Analytics Hub", code: "CAT-93", focus: "Strategic computational modelling of athletic spatial routing and biometric telemetry mechanics (100 capacity nodes)." },
        'CAT-94': { name: "Global Solver & Linear Programming Matrix", code: "CAT-94", focus: "Infinite-capacity optimization algorithms targeting NexoraX liquidity bottlenecks and asset logistics (100 solver nodes)." },
        'CAT-95': { name: "NexoraX Academy Curriculum Hub", code: "CAT-95", focus: "Interactive learning courses and micro-service testing architectures designed for complete system mastery (100 academy nodes)." },
        'CAT-96': { name: "Lead Systems Architect Core Node", code: "CAT-96", focus: "Absolute executive control, Secure Payment Provider reconciliation ledger rules, and continuous security orchestration (1 master architect core)." }
      }
    },
    NEXORAX_ZENITH: {
      blockTitle: "9. Protocol: NEXORAX_ZENITH_FINALIZATION_V1",
      baseTier: "VIP_SINGULARITY",
      deposit: "£25,000",
      depositAmount: 25000,
      subNodes: {
        'CAT-90': { name: "NexoraX Linguistics Hub & Babel Breaker", code: "CAT-90", focus: "Universal speech-to-speech translations, neural dialects and localized text corpus engines with 100 subcategory heuristics." },
        'CAT-91': { name: "Future Frontier Tech & Next-Gen Fusion", code: "CAT-91", focus: "Nuclear-grade computations, quantum server setups, and high-energy physics data modeling with 100 subcategory heuristics." },
        'CAT-97': { name: "NexoraX Governance Systems Integrity", code: "CAT-97", focus: "Zero-compromise automated public anti-corruption monitors and truth-indexed legal databases (100 subcategory heuristics)." },
        'CAT-98': { name: "Health & Biometric Truth Networks", code: "CAT-98", focus: "Decentralized wellness telemetry engines containing personal biometric profiles and physical health metrics (100 subcategory heuristics)." },
        'CAT-99': { name: "Environmental Stabilization Carbon Audits", code: "CAT-99", focus: "Global carbon ledger tracing mechanics, ecological emission grids, and NexoraX ecological audits (100 subcategory heuristics)." },
        'CAT-100': { name: "Omega Singularity Unified Heuristic Master Engine", code: "CAT-100", focus: "The ultimate cognitive synthetic overlay node coordinating the entire 18-sector imperial grid and global solvers (100 heuristic modules)." }
      }
    }
  };

  // Multiple-choice questions mapped per category
  const quizQuestionsByCategory: Record<'RESTORATION_CORE' | 'AI_SYSTEMS' | 'NETWORK_INFRASTRUCTURE' | 'COMMERCIAL_PIPELINES' | 'NEXORAX_REVENUE' | 'COMPLIANCE_STRIKE' | 'NEXORAX_INCUBATOR' | 'NEXORAX_ACADEMY' | 'NEXORAX_ZENITH', QuizQuestion[]> = {
    RESTORATION_CORE: [
      {
        id: 'rc_q1',
        question: "Which continuous background pipeline mirrors source folders under /var/www/nexorax_source to the secure Google Drive backup?",
        options: ["CloudSync-02", "Oracle-06", "Sentry-01", "Vanguard-03"],
        correctIndex: 0
      },
      {
        id: 'rc_q2',
        question: "Under active security monitoring, which geographic hosting shift coordinates are tracked for rogue brand replication attempts?",
        options: ["Localized clusters in India", "Distributed virtual arrays in Germany", "Residential servers in Canada", "Unassigned nodes in South Africa"],
        correctIndex: 0
      },
      {
        id: 'rc_q3',
        question: "What is the primary official contact channel for administrative ticket resolution for outside systems?",
        options: ["support@nexoraxdigital.co.uk", "admin@guardian-net.co.uk", "gavel-recov@nexorax.co.uk", "director@nexorax.org"],
        correctIndex: 0
      }
    ],
    AI_SYSTEMS: [
      {
        id: 'ai_q1',
        question: "Which government allocation protocol is utilized to completely fund direct Principal Private Secretary (Secure Invoice Register) acquisitions?",
        options: ["NexoraX billing records scheme", "National Research Council Grants", "Tier-2 Skilled Worker Sponsorship", "Enterprise Standards Health Surcharge Relief"],
        correctIndex: 0
      },
      {
        id: 'ai_q2',
        question: "What is the exact temporal duration specification for the 'God-Mode Funnel' utilized during initial qualification trials?",
        options: ["15-2-8 Minutes (Trial - Data Extraction - Critical Closure)", "10-5-5 Minutes (Discovery - Integration - Audit)", "30-5-10 Minutes (Setup - Evaluation - Transaction)", "20-1-9 Minutes (Onboarding - Verification - Handshake)"],
        correctIndex: 0
      },
      {
        id: 'ai_q3',
        question: "Who are the designated candidates tracked under the Chief of Staff Secure Invoice Register acquisition status index?",
        options: ["Claire, Helen, and Eleanor", "Staff Coordinator, Communications Associate, and Claire", "Staff Coordinator and Communications Associate only", "Communications Associate, Sarah, and Eleanor"],
        correctIndex: 0
      }
    ],
    NETWORK_INFRASTRUCTURE: [
      {
        id: 'ni_q1',
        question: "What is the official Planet Telecom voice frequency tracked for inbound client support and verbal negotiations?",
        options: ["08000 622827", "08000 11 22 33", "08800 65 43 21", "07359 062 464"],
        correctIndex: 0
      },
      {
        id: 'ni_q2',
        question: "To conform to the container reverse proxy infrastructure mapping, which exact host port must all server backends bind to?",
        options: ["Port 3000", "Port 80", "Port 8080", "Port 5173"],
        correctIndex: 0
      },
      {
        id: 'ni_q3',
        question: "Under Pakistan Remote Node Sync operations, which team members' compliance/migration files are tracked for sponsorships under SC887095?",
        options: ["Staff Coordinator and Communications Associate", "Communications Associate and Eleanor", "Staff Coordinator and Helen", "Staff Coordinator, Communications Associate, and Claire"],
        correctIndex: 0
      }
    ],
    COMMERCIAL_PIPELINES: [
      {
        id: 'cp_q1',
        question: "What is the non-negotiable Secure Payment Provider Sort Code and Account Number routing configuration for the NexoraX net settlement ledger?",
        options: ["04-**-** & ******15", "04-**-** & 51241512", "09-01-22 & 91285125", "04-**-** & 88709511"],
        correctIndex: 0
      },
      {
        id: 'cp_q2',
        question: "Under the Tiered Extraction Protocol, what is the exact percentage split evaluated as the 10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee) on remaining finance balances?",
        options: ["10% Maintenance & Hosting Fee", "1% 3% Community Allocation", "10% Standard Gavel Surcharge", "5% Transaction Levy"],
        correctIndex: 0
      },
      {
        id: 'cp_q3',
        question: "How is the 1% 3% Community Allocation divided for distribution back to general fund charities (dunfermline, global, and UK)?",
        options: ["Equal 33.33% split between the 3 designated entities", "100% directly routed to Charity 1 (Dunfermline)", "50% to Charity 1, 25% each to Charity 2 & 3", "Divided strictly based on weekly live ledger volume"],
        correctIndex: 0
      }
    ],
    NEXORAX_REVENUE: [
      {
        id: 'sr_q1',
        question: "What is the total value of the general Resource resource anchor integrated with the Nexorax NexoraX infrastructure?",
        options: ["£5.1 Million resource", "£1.0 Million resource", "£3.5 Million resource", "£10.0 Million resource"],
        correctIndex: 0
      },
      {
        id: 'sr_q2',
        question: "Which banking institution handles the net settlements for the Nexorax NexoraX finance?",
        options: ["Secure Payment Provider (04-**-** | ******15)", "Barclays Bank (20-00-00 | 11223344)", "HSBC UK (40-00-00 | 99887766)", "Lloyds Bank (30-00-00 | 55667788)"],
        correctIndex: 0
      },
      {
        id: 'sr_q3',
        question: "How is Charity 3 (United Kingdom) split residue calculated when dividing the 1% 3% Community Allocation?",
        options: ["Remaining balance after subtracting Dunfermline and Global splits", "Flat rate of exactly £1,000 per daily transaction", "Evaluated strictly as 40% of the gross 3% Community Allocation", "Split proportionally based on localized IP coordinates in India"],
        correctIndex: 0
      }
    ],
    COMPLIANCE_STRIKE: [
      {
        id: 'is_q1',
        question: "What is the official sponsorship license registration number for Nexorax Digital Ltd?",
        options: ["SC887095", "SC887000", "SC112233", "UKVI-887"],
        correctIndex: 0
      },
      {
        id: 'is_q2',
        question: "Who are the primary targets tracked under the NexoraX enterprise standards node files?",
        options: ["Staff Coordinator and Communications Associate", "Staff Coordinator and Helen", "Communications Associate and Eleanor", "Staff Coordinator, Communications Associate, and Claire"],
        correctIndex: 0
      },
      {
        id: 'is_q3',
        question: "If compliance clearance exceeds the 15-day SLA, what automated directive is triggered?",
        options: ["Gavel Strike Institutional Inquiry Mandate", "Manual email escalation process", "Lid-open secondary authentication request", "Compliance application refund claim submission"],
        correctIndex: 0
      }
    ],
    NEXORAX_INCUBATOR: [
      {
        id: 'si_q1',
        question: "Which Category number is assigned to the NexoraX Incubator node matrix within the Nexorax infrastructure?",
        options: ["Category 91", "Category 15", "Category 7", "Category 1"],
        correctIndex: 0
      },
      {
        id: 'si_q2',
        question: "What is the primary function of the Automated Inventory Sink (Node-91-01)?",
        options: ["Real-time sync of warehouse ledger assets under the control of Chronos-Ledger nodes", "Executing automated PR pitches to client lists on Friday", "Rerouting telephone trunk lines to the Director's direct numbers", "Verifying compliance priority sponsorships over 15-day periods"],
        correctIndex: 0
      },
      {
        id: 'si_q3',
        question: "What are the core parameters for the NexoraX Incubator Matrix deployment?",
        options: ["30-Node Matrix deployment with 60-Second Temporal Lock & Secure Payment Provider settlements", "10-Node Matrix with manual CSV batch uploads", "5-Node Matrix with zero local key protection", "50-Node matrix with unmetered public API keys"],
        correctIndex: 0
      }
    ],
    NEXORAX_ACADEMY: [
      {
        id: 'sa_q1',
        question: "Under CAT-93 Football Engineering, what is the focus area of strategic spatial optimization?",
        options: ["Athletic spatial routing, positioning, and biometric telemetry mechanics", "Designing soccer stadium seating layouts", "Managing seasonal ticketing pipelines on Stripe", "Streaming direct matches to the remote Pakistani nodes"],
        correctIndex: 0
      },
      {
        id: 'sa_q2',
        question: "What is the primary solving capacity metric configured under the CAT-94 Global Solver?",
        options: ["Infinite-capacity linear programming for liquidity & logistics routing", "Simple arithmetic calculations for daily gross harvests", "Standard NexoraX billing records eligibility checks", "Continuous mirroring of directories via CloudSync-02"],
        correctIndex: 0
      },
      {
        id: 'sa_q3',
        question: "Under the NexoraXty Hub, which node is responsible for absolute centralized architectural governance?",
        options: ["CAT-96 Lead Systems Architect Core", "CAT-95 NexoraX Academy Portal", "CAT-93 Football Engineering Engine", "CAT-94 Linear Programming Grid"],
        correctIndex: 0
      }
    ],
    NEXORAX_ZENITH: [
      {
        id: 'sz_q1',
        question: "Under the NexoraX Zenith Matrix, how many subcategory heuristic engines are deployed per master node?",
        options: ["100-Subcategory Heuristic Engines", "10-Subcategory Standard Models", "5-Subcategory Basic Templates", "No subcategories; single unified layers only"],
        correctIndex: 0
      },
      {
        id: 'sz_q2',
        question: "Which Master Node functions as the ultimate Unified Cognitive Heuristic Master Engine?",
        options: ["CAT-100 Omega Singularity Engine", "CAT-90 NexoraX Linguistics Node", "CAT-97 NexoraX Governance Unit", "CAT-99 Environmental Audit Matrix"],
        correctIndex: 0
      },
      {
        id: 'sz_q3',
        question: "What is the mission objective mapped directly to the CAT-97 NexoraX Governance master node?",
        options: ["Truth-Based Administrative Operations & Anti-Corruption Audits", "Tracking physical biometric athlete routing paths", "Synthesis of localized customer pitch brochures", "Automated distribution of raw gross financial harvests"],
        correctIndex: 0
      }
    ]
  };

  const handleBlockChange = (blockKey: 'RESTORATION_CORE' | 'AI_SYSTEMS' | 'NETWORK_INFRASTRUCTURE' | 'COMMERCIAL_PIPELINES' | 'NEXORAX_REVENUE' | 'COMPLIANCE_STRIKE' | 'NEXORAX_INCUBATOR' | 'NEXORAX_ACADEMY' | 'NEXORAX_ZENITH') => {
    setActiveBlock(blockKey);
    const firstSubNodeKey = Object.keys(trainingMatrix[blockKey].subNodes)[0];
    setSelectedSubNode(firstSubNodeKey);
  };

  const handleInitializeTraining = () => {
    const block = trainingMatrix[activeBlock];
    const subNode = block.subNodes[selectedSubNode];
    if (typeof (window as any).triggerNexoraXPayment === 'function') {
      (window as any).triggerNexoraXPayment({
        amount: block.depositAmount,
        clientName: `NexoraX Training - ${subNode.name} (${subNode.code})`,
        invoiceRef: `NX-TRAIN-${activeBlock}-${subNode.code}-${Math.floor(100 + Math.random() * 900)}`,
        channel: 'SecurePayment'
      });
    } else {
      alert(`Handshake initialized: Training for ${subNode.name} (${subNode.code}). Activation deposit: ${block.deposit}. Net Routed to Secure Payment Provider Sort Code 04-**-**.`);
    }
  };

  const handleTriggerBypass = (price: number, actionLabel: string) => {
    if (typeof (window as any).triggerNexoraXPayment === 'function') {
      (window as any).triggerNexoraXPayment({
        amount: price,
        clientName: `NexoraX Unlock - ${actionLabel}`,
        invoiceRef: `NX-BYPASS-${Math.floor(1000 + Math.random() * 9000)}`,
        channel: 'SecurePayment'
      });
    } else {
      alert(`Bypass Command triggered: ${actionLabel} - Price: £${price}. Reconciling on SecurePayment Ledger.`);
    }
  };

  const handleResetLock = () => {
    setTrialTimeLeft(60);
    setIsLocked(false);
  };

  // CAT-90 NexoraX Linguistics & Automation Node Helper Functions
  const handleLingChatSend = async () => {
    if (!lingChatInput.trim() || isLingResponding) return;
    const userMsg = lingChatInput;
    setLingChatInput("");
    setLingChatHistory(prev => [...prev, { role: 'user', text: userMsg }]);
    setIsLingResponding(true);

    try {
      const response = await fetch("/api/voice/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: `[Tutor Context Directive: Specialized English standard IELTS/UK testing & Fife Dialect Coach simulation request]: ${userMsg}`,
          history: lingChatHistory.slice(1).map(h => h.text),
          mode: "concierge"
        })
      });
      const data = await response.json();
      if (data && data.text) {
        setLingChatHistory(prev => [...prev, { role: 'tutor', text: data.text }]);
      } else {
        setLingChatHistory(prev => [...prev, { role: 'tutor', text: "Linguistics Hub processed and structured English standard correction vectors successfully." }]);
      }
    } catch (err) {
      console.error("Linguistics tutor chat failed:", err);
      setLingChatHistory(prev => [...prev, { role: 'tutor', text: "Linguistics routing buffer bottleneck. Re-trying node communication..." }]);
    } finally {
      setIsLingResponding(false);
    }
  };

  const translateFifeToEnglish = (text: string) => {
    const fifeInputLower = text.toLowerCase();
    let explanation = "";
    let translation = "";

    if (fifeInputLower.includes("bairn")) {
      translation = "Child or baby";
      explanation = "e.g., 'Look at the wee bairn!' -> 'Look at the small baby!' Heavily used across Fife, Scotland to refer to infants or young ones.";
    } else if (fifeInputLower.includes("crabbit")) {
      translation = "Grumpy, bad-tempered, or cross";
      explanation = "e.g., 'He's awfy crabbit the day.' -> 'He is very grumpy today.' Describes someone in a sour or irritable mood.";
    } else if (fifeInputLower.includes("braw")) {
      translation = "Good, fine, or handsome/beautiful";
      explanation = "e.g., 'It's a braw day!' -> 'It is a beautiful/fine day today!' A classic Scots praise word used frequently in the local Fife dialect.";
    } else if (fifeInputLower.includes("fankle")) {
      translation = "Tangle or muddle";
      explanation = "e.g., 'The wires are in a right fankle!' -> 'The wires are in a complete tangle!' Refers to physical tangles or confusing situations.";
    } else if (fifeInputLower.includes("ken")) {
      translation = "Know";
      explanation = "e.g., 'Wee Jim, d'ye ken him?' -> 'Little Jim, do you know him?' 'Ken' is one of the most famous dialect markers in East Scotland.";
    } else if (fifeInputLower.includes("dinnae")) {
      translation = "Do not";
      explanation = "e.g., 'I dinnae ken.' -> 'I do not know.' Simple negation contraction typical of the local region.";
    } else if (fifeInputLower.includes("yin")) {
      translation = "One";
      explanation = "e.g., 'That's the yin.' -> 'That is the one.' Refers to a single item or person.";
    } else if (fifeInputLower.includes("tatties")) {
      translation = "Potatoes";
      explanation = "e.g., 'Mince and tatties.' -> 'Minced beef and potatoes.' A beloved staple Scottish dish.";
    } else if (fifeInputLower.includes("neeps")) {
      translation = "Turnips / Swedes";
      explanation = "e.g., 'Haggis, neeps and tatties.' -> 'Haggis, turnips and potatoes.' Essential national culinary term.";
    } else {
      translation = "Translating dialect node...";
      explanation = "Linguistics Hub (CAT-90) identifies this as a potential Fife/UK colloquialism. Practice standard IELTS English equivalents or try terms like 'bairn', 'crabbit', 'braw', 'ken', or 'dinnae'.";
    }

    setFifeOutput(`Translation: "${translation}"\n\nExplanation: ${explanation}`);
  };

  const runAutomationController = () => {
    setAutomationStatus('running');
    setAutomationEmailDraft("");
    setAutomationLogs([
      `[ARCHITECT] Initialization sequence started.`,
      `[ARCHITECT] Binding to Category Vector: "${automationCategory}"`,
      `[ARCHITECT] Mapping sub-node endpoint: "${automationSubNode}"`,
      `[ARCHITECT] Loading customer telemetry: "${automationCustomerName}"`,
      `[SYSTEM] Invoking: automateCustomerWorkflow(clientNode)...`
    ]);

    setTimeout(() => {
      const clientNode = {
        category: automationCategory,
        subNode: automationSubNode,
        customerName: automationCustomerName
      };

      const compiledDraft = `Hello ${clientNode.customerName}, regarding your interest in ${clientNode.category} - ${clientNode.subNode}...`;
      
      setAutomationEmailDraft(compiledDraft);
      setAutomationLogs(prev => [
        ...prev,
        `[CONSOLE-LOG] Automating communication for node: ${clientNode.category}`,
        `[SYSTEM] Workflow complete. Generated Email Draft has been locked to secure cache.`,
        `[SECURE] Verification fingerprint: NX-AUTO-90-${Math.floor(1000 + Math.random() * 9000)}`
      ]);
      setAutomationStatus('success');

      if (typeof (window as any).showCosmicToast === 'function') {
        (window as any).showCosmicToast("Customer workflow executed successfully via Node-90", "success");
      } else {
        console.log("Customer workflow executed successfully via Node-90.");
      }
    }, 1200);
  };

  // Initialize Simulator Sandbox Session
  const handleLaunchSandbox = () => {
    const calculationOptions = [90000, 150000, 240000, 300000, 450000, 600000];
    const chosenVal = calculationOptions[Math.floor(Math.random() * calculationOptions.length)];
    
    setGrossHarvestSample(chosenVal);
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizScore(0);
    setQuizPass(false);
    setCalcSuccess(null);
    setCalcErrors([]);
    setCalculationInputs({
      levy: '',
      split: '',
      gavelFee: '',
      net: ''
    });
    setVaultSyncStatus('idle');
    setCertLogHash(`NX-CERT-2026-${activeSubNode.code}-${Math.floor(10000 + Math.random() * 89999)}`);
    setSandboxStep('LESSON');
    setSandboxActive(true);
  };

  // Select answering index
  const handleAnswerSelect = (questionId: string, optionIndex: number) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionId]: optionIndex
    }));
  };

  // Desk calculator evaluation
  const handleCalculatorPress = (char: string) => {
    if (char === 'C') {
      setCalcDisplay('');
      setCalcResult('');
    } else if (char === '=') {
      try {
        // Safe evaluation of basic arithmetic
        const sanitized = calcDisplay.replace(/[^0-9+\-*/.]/g, '');
        // eslint-disable-next-line no-eval
        const result = eval(sanitized);
        if (result !== undefined) {
          setCalcResult(String(result));
        }
      } catch (err) {
        setCalcResult('Error');
      }
    } else {
      setCalcDisplay(prev => prev + char);
    }
  };

  // Submit diagnostic quiz and accounting calculations
  const handleSubmitQuiz = () => {
    const activeQuestions = quizQuestionsByCategory[activeBlock];
    let correctCount = 0;
    
    activeQuestions.forEach(q => {
      if (quizAnswers[q.id] === q.correctIndex) {
        correctCount++;
      }
    });

    const isQuestionsPerfect = correctCount === activeQuestions.length;
    
    // Evaluate accounting math
    const inputLevy = parseFloat(calculationInputs.levy.trim().replace(/,/g, ''));
    const inputSplit = parseFloat(calculationInputs.split.trim().replace(/,/g, ''));
    const inputGavel = parseFloat(calculationInputs.gavelFee.trim().replace(/,/g, ''));
    const inputNet = parseFloat(calculationInputs.net.trim().replace(/,/g, ''));

    const expectedLevy = grossHarvestSample * 0.01;
    const expectedSplit = expectedLevy / 3;
    const remaining = grossHarvestSample - expectedLevy;
    const expectedGavel = remaining * 0.20;
    const expectedNet = remaining * 0.80;

    const errors: string[] = [];

    if (isNaN(inputLevy) || Math.abs(inputLevy - expectedLevy) > 2.0) {
      errors.push(`3% Community Allocation (1%) is incorrect. Input: £${calculationInputs.levy || '0'}. Expected: £${expectedLevy.toLocaleString('en-GB')}`);
    }
    if (isNaN(inputSplit) || Math.abs(inputSplit - expectedSplit) > 2.0) {
      errors.push(`Charity 1 Split (33.33% of Levy) is incorrect. Input: £${calculationInputs.split || '0'}. Expected: £${expectedSplit.toFixed(2)}`);
    }
    if (isNaN(inputGavel) || Math.abs(inputGavel - expectedGavel) > 2.0) {
      errors.push(`10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee on £${remaining.toLocaleString('en-GB')}) is incorrect. Input: £${calculationInputs.gavelFee || '0'}. Expected: £${expectedGavel.toLocaleString('en-GB')}`);
    }
    if (isNaN(inputNet) || Math.abs(inputNet - expectedNet) > 2.0) {
      errors.push(`Net finance Settlement routed to SecurePayment (remaining 80%) is incorrect. Input: £${calculationInputs.net || '0'}. Expected: £${expectedNet.toLocaleString('en-GB')}`);
    }

    setCalcErrors(errors);
    const isMathPerfect = errors.length === 0;

    setQuizScore(correctCount);
    setQuizSubmitted(true);
    setCalcSuccess(isMathPerfect);

    if (isQuestionsPerfect && isMathPerfect) {
      setQuizPass(true);
      setSandboxStep('CERTIFICATE');
    } else {
      setQuizPass(false);
    }
  };

  // Save Certification record securely to Firestore/localStorage
  const handleSaveCertificateToVault = async () => {
    setCertifying(true);
    setVaultSyncStatus('syncing');
    
    const certRecord = {
      traineeName: traineeName,
      subNodeName: activeSubNode.name,
      subNodeCode: activeSubNode.code,
      evaluationScore: `${quizScore} / 3`,
      grossAuditSample: grossHarvestSample,
      certificationHash: certLogHash,
      dateCompleted: new Date().toISOString(),
      userEmail: auth.currentUser?.email || 'siddique.mahmood@gmail.com',
      userId: auth.currentUser?.uid || 'guest_system'
    };

    try {
      // Direct Firestore write block matching the rule parameters
      await addDoc(collection(db, 'saved_certifications'), {
        ...certRecord,
        syncedAt: new Date().toISOString()
      });
      setVaultSyncStatus('success');
      setTimeout(() => setVaultSyncStatus('idle'), 4000);
    } catch (error: any) {
      console.warn("Direct Cloud write fallback to local storage buffer: ", error);
      // Fallback local storage secure dump in structural format
      try {
        const localCerts = JSON.parse(localStorage.getItem('nexorax_local_certs') || '[]');
        localCerts.push(certRecord);
        localStorage.setItem('nexorax_local_certs', JSON.stringify(localCerts));
        setVaultSyncStatus('success');
      } catch (lError) {
        setVaultSyncStatus('error');
      }
    } finally {
      setCertifying(false);
    }
  };

  const handlePrintCertificate = () => {
    window.print();
  };

  const speakZenith = (text: string) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.0;
      utterance.pitch = 0.95;
      window.speechSynthesis.speak(utterance);
    }
  };

  const handleDeployNexoraXZenith = () => {
    if (isZenithActive) return;
    setIsZenithActive(true);
    setZenithStatus('initializing');
    setZenithProgress(5);
    setZenithConsole(["[NEXORAX-SYSTEM] Initializing Zenith Matrix..."]);

    speakZenith("Initializing NexoraX Zenith Matrix. Aligning heuristic engines.");

    const steps = [
      {
        progress: 15,
        log: "[NEXORAX-SYSTEM] Node CAT-90 (NEXORAX LINGUISTICS) registered. Deploying 100-Subcategory Heuristic Engine...",
        speak: "Category ninety. NexoraX Linguistics deployed."
      },
      {
        progress: 30,
        log: "[NEXORAX-SYSTEM] Node CAT-91 (FUTURE FRONTIER TECH) registered. Deploying 100-Subcategory Heuristic Engine...",
        speak: "Category ninety one. Future Frontier Tech deployed."
      },
      {
        progress: 45,
        log: "[NEXORAX-SYSTEM] Node CAT-97 (NEXORAX GOVERNANCE) registered. Deploying 100-Subcategory Heuristic Engine...",
        speak: "Category ninety seven. NexoraX Governance deployed."
      },
      {
        progress: 60,
        log: "[NEXORAX-SYSTEM] Node CAT-98 (HEALTH & BIOMETRIC TRUTH) registered. Deploying 100-Subcategory Heuristic Engine...",
        speak: "Category ninety eight. Health and Biometric Truth deployed."
      },
      {
        progress: 75,
        log: "[NEXORAX-SYSTEM] Node CAT-99 (ENVIRONMENTAL STABILIZATION) registered. Deploying 100-Subcategory Heuristic Engine...",
        speak: "Category ninety s-nine. Environmental Stabilization deployed."
      },
      {
        progress: 90,
        log: "[NEXORAX-SYSTEM] Node CAT-100 (OMEGA SINGULARITY) registered. Deploying 100-Subcategory Heuristic Engine...",
        speak: "Category one hundred. Omega Singularity deployed."
      },
      {
        progress: 95,
        log: "[NEXORAX-SYSTEM] Running final systems synchronizations with global invoice ledger databases...",
        speak: "Synchronizing global ledger."
      },
      {
        progress: 100,
        log: "[NEXORAX-SYSTEM] Master Heuristic Bridge activated. NEXORAX ZENITH MATRIX ONLINE. FULL COMMAND GRANTED.",
        speak: "Zenith Matrix is now online. Full command has been granted."
      }
    ];

    steps.forEach((step, index) => {
      setTimeout(() => {
        setZenithProgress(step.progress);
        setZenithConsole(prev => [...prev, step.log]);
        if (step.speak) {
          speakZenith(step.speak);
        }
        if (step.progress === 100) {
          setZenithStatus('completed');
          setIsZenithActive(false);
        }
      }, (index + 1) * 1200);
    });
  };

  // Get current active sub-node details
  const activeSubNode = trainingMatrix[activeBlock].subNodes[selectedSubNode] || Object.values(trainingMatrix[activeBlock].subNodes)[0];

  return (
    <div id="NexoraX-master-training-root" className="w-full bg-black min-h-[85vh] text-slate-100 rounded-3xl border border-slate-800 p-6 md:p-8 relative overflow-hidden flex flex-col space-y-8">
      {/* Background neon grid lines */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#0f172a_1px,transparent_1px),linear-gradient(to_bottom,#0f172a_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-35 pointer-events-none"></div>

      {/* Ticker bar and header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-800 pb-6 relative z-10">
        <div>
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#f59e0b] shadow-[0_0_10px_rgba(245,158,11,0.2)]" />
            <h1 className="text-xl md:text-2xl font-sans font-black text-white tracking-widest uppercase">
              NEXORAX 1-TO-90 CATEGORY TRAINING ENGINE
            </h1>
          </div>
          <p className="text-[10px] font-mono text-slate-500 mt-1 uppercase tracking-[0.2em]">
            NexoraX Deployment Training Matrix // Version 1.10 LIVE-RUNNER
          </p>
        </div>
        
        {/* Temporal Lock Ticker */}
        <div className="flex items-center gap-3 shrink-0">
          {!sandboxActive && isLocked && (
            <button 
              onClick={handleResetLock}
              className="px-3 py-1.5 border border-slate-700 bg-slate-900/50 hover:bg-slate-900 text-slate-300 font-mono text-[9px] uppercase font-bold tracking-wider rounded-lg transition-all"
            >
              Demo Reset
            </button>
          )}
          <div className={`px-4 py-2 border rounded-xl font-mono text-xs font-bold tracking-widest transition-all duration-300 flex items-center gap-2 ${
            sandboxActive
              ? 'border-indigo-500 bg-indigo-950/40 text-indigo-400'
              : isLocked 
                ? 'border-red-600 bg-red-950/40 text-red-400 animate-pulse' 
                : 'border-amber-500 bg-amber-950/40 text-[#f59e0b]'
          }`}>
            <Clock className={`w-3.5 h-3.5 ${isLocked && !sandboxActive ? 'animate-spin' : ''}`} />
            <span>
              {sandboxActive 
                ? "ACTIVE TRAINING SANDBOX ISOLATION NODE" 
                : isLocked 
                  ? "ACCESS SUSPENDED: INITIALIZE TRAINING TIER" 
                  : `TEMPORAL DEMO STREAM: 00:${trialTimeLeft < 10 ? '0' + trialTimeLeft : trialTimeLeft}`}
            </span>
          </div>
        </div>
      </div>

      {!sandboxActive && (
        <div className="flex flex-wrap gap-2 border-b border-slate-900 pb-4 z-10 relative">
          <button
            onClick={() => setActiveMode('MATRIX')}
            className={`px-4 py-2 font-mono text-[9px] font-black uppercase tracking-widest rounded-lg border transition-all cursor-pointer ${
              activeMode === 'MATRIX'
                ? 'border-amber-500 bg-amber-950/20 text-[#f59e0b]'
                : 'border-[#1e293b] text-slate-500 bg-transparent hover:text-slate-300 hover:border-slate-800'
            }`}
          >
            1. Training Matrix Blocks
          </button>
          <button
            onClick={() => setActiveMode('EXPLORER')}
            className={`px-4 py-2 font-mono text-[9px] font-black uppercase tracking-widest rounded-lg border transition-all cursor-pointer ${
              activeMode === 'EXPLORER'
                ? 'border-indigo-500 bg-indigo-950/20 text-indigo-400'
                : 'border-[#1e293b] text-slate-500 bg-transparent hover:text-slate-300 hover:border-slate-800'
            }`}
          >
            2. 1-to-100 Category Database Explorer (CAT-90 Core)
          </button>
          <button
            onClick={() => setActiveMode('LIST')}
            className={`px-4 py-2 font-mono text-[9px] font-black uppercase tracking-widest rounded-lg border transition-all cursor-pointer ${
              activeMode === 'LIST'
                ? 'border-rose-500 bg-rose-950/20 text-rose-400'
                : 'border-[#1e293b] text-slate-500 bg-transparent hover:text-slate-300 hover:border-slate-800'
            }`}
          >
            3. 1-to-100 Formatted Core List
          </button>
          <button
            onClick={() => setActiveMode('DOMINION')}
            className={`px-4 py-2 font-mono text-[9px] font-black uppercase tracking-widest rounded-lg border transition-all cursor-pointer ${
              activeMode === 'DOMINION'
                ? 'border-emerald-500 bg-emerald-950/20 text-[#10b981]'
                : 'border-[#1e293b] text-slate-500 bg-transparent hover:text-slate-300 hover:border-slate-800'
            }`}
          >
            4. Dominion Harvest & Staff board
          </button>
          <button
            onClick={() => setActiveMode('REG_INQUIRY')}
            className={`px-4 py-2 font-mono text-[9px] font-black uppercase tracking-widest rounded-lg border transition-all cursor-pointer ${
              activeMode === 'REG_INQUIRY'
                ? 'border-cyan-500 bg-cyan-950/20 text-cyan-400'
                : 'border-[#1e293b] text-slate-500 bg-transparent hover:text-slate-300 hover:border-slate-800'
            }`}
          >
            5. Regulatory Solver Node
          </button>
        </div>
      )}

      <AnimatePresence mode="wait">
        {!sandboxActive ? (
          activeMode === 'MATRIX' ? (
            /* Standard Matrix Browser Display */
            <motion.div 
              key="matrix-browser"
              className="grid grid-cols-1 lg:grid-cols-12 gap-6 relative z-10 flex-1"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.25 }}
            >
            {/* Left Column (Grid Span 4): Infrastructure Blocks */}
            <div className="lg:col-span-4 flex flex-col space-y-3">
              <span className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest px-2">
                Infrastructure Blocks
              </span>
              <div className="space-y-2">
                {(Object.keys(trainingMatrix) as Array<'RESTORATION_CORE' | 'AI_SYSTEMS' | 'NETWORK_INFRASTRUCTURE' | 'COMMERCIAL_PIPELINES' | 'NEXORAX_REVENUE' | 'COMPLIANCE_STRIKE' | 'NEXORAX_INCUBATOR' | 'NEXORAX_ACADEMY' | 'NEXORAX_ZENITH'>).map((blockKey) => {
                  const blockDetail = trainingMatrix[blockKey];
                  const isSelected = activeBlock === blockKey;
                  return (
                    <button
                      key={blockKey}
                      onClick={() => { if (!isLocked) handleBlockChange(blockKey); }}
                      disabled={isLocked}
                      className={`w-full text-left p-4 rounded-xl border transition-all duration-200 text-xs font-mono uppercase tracking-wider flex flex-col justify-between ${
                        isLocked 
                          ? 'border-slate-900 text-slate-600 bg-slate-950/30 cursor-not-allowed' 
                          : isSelected 
                            ? 'border-[#f59e0b] bg-slate-950 text-white shadow-[0_0_20px_rgba(245,158,11,0.08)] font-black' 
                            : 'border-slate-800 text-slate-400 bg-black/40 hover:border-slate-700 hover:text-slate-200'
                      }`}
                    >
                      <span className="font-extrabold text-slate-200 tracking-wide">{blockDetail.blockTitle}</span>
                      <div className="flex items-center justify-between text-[8px] text-slate-500 mt-2 font-bold tracking-widest">
                        <span>TIER: {blockDetail.baseTier}</span>
                        <span className="text-[#f59e0b]">DEPOSIT: {blockDetail.deposit}</span>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="p-4 rounded-2xl bg-[#09090b] border border-slate-900 space-y-3 font-mono text-[9px] uppercase tracking-wider text-slate-500 mt-2">
                <div className="flex items-center gap-1.5 text-slate-400 font-bold">
                  <Shield className="w-3.5 h-3.5 text-indigo-500" />
                  <span>National Security Core</span>
                </div>
                <p className="leading-relaxed text-[8.5px]">
                  Access standard rules aligned to UK NI: NH******B, direct billing mechanisms and Secure Payment Provider routes (Sort: 04-**-**, Acc: ******15). Logged to NexoraX Resource Library.
                </p>
              </div>
            </div>

            {/* Middle Column (Grid Span 4): Specific Category Sub-Nodes Directory */}
            <div className="lg:col-span-4 flex flex-col space-y-3">
              <span className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest px-2">
                Sub-Node Training Vectors ({Object.keys(trainingMatrix[activeBlock].subNodes).length})
              </span>
              <div className="space-y-2 overflow-y-auto max-h-[55vh] lg:max-h-[60vh] pr-1">
                {Object.keys(trainingMatrix[activeBlock].subNodes).map((nodeKey) => {
                  const subNode = trainingMatrix[activeBlock].subNodes[nodeKey];
                  const isSelected = selectedSubNode === nodeKey;
                  return (
                    <button
                      key={nodeKey}
                      onClick={() => { if (!isLocked) setSelectedSubNode(nodeKey); }}
                      disabled={isLocked}
                      className={`w-full text-left p-3.5 rounded-xl border transition-all duration-200 flex flex-col gap-1 text-xs font-mono relative ${
                        isLocked 
                          ? 'border-slate-950 text-slate-700 bg-slate-950/20 cursor-not-allowed' 
                          : isSelected 
                            ? 'border-white bg-slate-900 text-white font-extrabold shadow-inner' 
                            : 'border-slate-900 text-slate-400 bg-black/30 hover:border-slate-800 hover:text-slate-200'
                      }`}
                    >
                      <div className="flex items-center justify-between w-full">
                        <span className="text-[9px] text-[#f59e0b] font-black">{nodeKey}</span>
                        <span className="text-[8px] px-1 py-0.5 rounded bg-black text-slate-500 border border-slate-900 font-bold">{subNode.code}</span>
                      </div>
                      <span className="truncate tracking-wide text-slate-200 pr-4 mt-0.5">{subNode.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right Column (Grid Span 4): Showcase Content Canvas & Direct Route */}
            <div className="lg:col-span-4 bg-gradient-to-br from-slate-950 to-black rounded-2xl border border-slate-900 p-6 relative overflow-hidden flex flex-col justify-between min-h-[440px]">
              
              {/* Locked Overlord Overlay */}
              <AnimatePresence>
                {isLocked && (
                  <motion.div 
                    initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
                    animate={{ opacity: 1, backdropFilter: 'blur(8px)' }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-black/95 flex flex-col justify-center items-center p-6 text-center z-50 rounded-2xl"
                  >
                    <div className="w-12 h-12 rounded-full border border-red-500 bg-red-950/30 flex justify-center items-center mb-4 text-red-500 animate-pulse">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                    </div>
                    <h3 className="text-sm font-mono font-black tracking-widest uppercase text-red-500">
                      Trial Context Frozen
                    </h3>
                    <p className="text-slate-400 font-mono text-[9px] max-w-xs mt-2 mb-6 uppercase tracking-wider leading-relaxed">
                      The 60-second instructional preview has concluded. System lock triggered under NexoraX Billing Protocol V1.0 constraints.
                    </p>
                    
                    <div className="flex flex-col gap-2 w-full max-w-xs">
                      <button 
                        onClick={() => handleTriggerBypass(199, '7-Day Sprint')}
                        className="w-full py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-mono text-[9px] font-black uppercase tracking-widest rounded transition-all cursor-pointer font-bold"
                      >
                        7-Day Sprint (£199)
                      </button>
                      <button 
                        onClick={() => handleTriggerBypass(299, '15-Day Expansion')}
                        className="w-full py-2.5 border border-white text-white font-mono text-[9px] font-black uppercase tracking-widest rounded hover:bg-white hover:text-black transition-all cursor-pointer"
                      >
                        15-Day Expansion (£299)
                      </button>
                      <button 
                        onClick={() => handleTriggerBypass(699, '30-Day Mastery')}
                        className="w-full py-2.5 bg-red-950/40 border border-red-700 text-red-400 font-mono text-[9px] font-black uppercase tracking-widest rounded hover:bg-red-900 hover:text-white transition-all cursor-pointer"
                      >
                        30-Day Mastery (£699)
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Active Canvas Layout */}
              {activeBlock === 'NEXORAX_ZENITH' ? (
                <div className="space-y-4 flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-center text-[9px] font-mono text-slate-500 border-b border-slate-900 pb-3 mb-2 uppercase tracking-wider font-bold">
                      <span>Zenith Core Spectrum</span>
                      <span className="text-[#f59e0b] font-black">CAT-90-100 MATRIX</span>
                    </div>

                    <h3 className="text-sm font-sans font-black text-white uppercase tracking-wider mb-2">
                      NexoraX Zenith Deployment
                    </h3>
                    <p className="text-slate-400 font-sans text-[11px] leading-relaxed">
                      Deploy all Master Heuristic nodes in the 90-to-100 spectrum with 100-subcategory processing engines.
                    </p>
                  </div>

                  {/* Visual Progress Bar if active or done */}
                  {(zenithStatus === 'initializing' || zenithStatus === 'completed') && (
                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center text-[9px] font-mono">
                        <span className="text-slate-500 uppercase font-bold">Heuristic Integration</span>
                        <span className="text-[#f59e0b] font-black">{zenithProgress}%</span>
                      </div>
                      <div className="w-full bg-slate-900 border border-slate-800 rounded-full h-2 overflow-hidden">
                        <div 
                          className="bg-gradient-to-r from-amber-500 to-[#f59e0b] h-full rounded-full transition-all duration-300" 
                          style={{ width: `${zenithProgress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Scrollable Live Console */}
                  <div className="bg-black/80 border border-slate-900 rounded-xl p-3 h-48 font-mono text-[9px] text-[#f59e0b] overflow-y-auto space-y-1.5 flex flex-col justify-start mt-2">
                    {zenithConsole.length === 0 ? (
                      <div className="text-slate-600 italic">Console idle. Awaiting authorization command...</div>
                    ) : (
                      zenithConsole.map((log, idx) => (
                        <div key={idx} className="leading-normal">
                          {log}
                        </div>
                      ))
                    )}
                  </div>

                  <div className="border-t border-slate-900 pt-4 mt-3 flex flex-col gap-2">
                    {zenithStatus !== 'completed' ? (
                      <button
                        onClick={handleDeployNexoraXZenith}
                        disabled={isZenithActive}
                        className={`w-full py-2.5 rounded-lg font-mono text-[9px] font-black uppercase tracking-wider transition-all duration-200 border cursor-pointer flex items-center justify-center gap-2 ${
                          isZenithActive
                            ? 'bg-slate-900/40 border-slate-800 text-slate-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black border-amber-600 font-extrabold shadow-[0_0_15px_rgba(245,158,11,0.2)]'
                        }`}
                      >
                        <Cpu className={`w-3.5 h-3.5 ${isZenithActive ? 'animate-spin' : ''}`} />
                        <span>{isZenithActive ? 'Executing Zenith Deployment...' : 'Deploy Zenith Heuristics (CAT 90-100)'}</span>
                      </button>
                    ) : (
                      <div className="text-center bg-emerald-950/25 border border-emerald-500/30 rounded-lg p-2.5 space-y-1">
                        <div className="text-[10px] text-emerald-400 font-bold tracking-widest uppercase font-mono flex items-center justify-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                          <span>Zenith Deploy Complete</span>
                        </div>
                        <p className="text-[8.5px] text-slate-400 font-sans">
                          NexoraX Heuristics aligned completely. Net settles routed to Secure Payment Provider.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-5">
                    <div className="flex justify-between items-center text-[9px] font-mono text-slate-500 border-b border-slate-900 pb-3 mb-2 uppercase tracking-wider font-bold">
                      <span>Syllabus Allocation</span>
                      <span className="text-[#f59e0b] font-black">{activeSubNode.code}</span>
                    </div>

                    <div>
                      <span className="text-[8px] font-mono text-slate-500 uppercase tracking-widest mb-1.5 block">SELECTED SUB-NODE VECTOR:</span>
                      <h3 className="text-sm md:text-base font-sans font-black text-white uppercase tracking-wider leading-snug">
                        {activeSubNode.name}
                      </h3>
                      <p className="text-slate-400 font-sans text-[11px] mt-2.5 leading-relaxed">
                        {activeSubNode.focus}
                      </p>
                    </div>

                    <div className="space-y-2 pt-1">
                      <span className="text-[8px] font-mono font-black text-slate-500 uppercase tracking-wider block">
                        TARGET METRICS COVERED:
                      </span>
                      <div className="bg-slate-900/40 border border-slate-900/80 p-3 rounded-xl space-y-2">
                        <div className="flex items-center gap-2 text-[10px] text-slate-300">
                          <CheckCircle2 className="w-3.5 h-3.5 text-[#f59e0b] shrink-0" />
                          <span className="font-sans">Complete Team Operational Competency</span>
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-slate-300">
                          <CheckCircle2 className="w-3.5 h-3.5 text-[#f59e0b] shrink-0" />
                          <span className="font-sans">Infrastructure Latency Reduced to 0.8s</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-slate-900 pt-5 mt-4 flex flex-col gap-3">
                    <div className="flex justify-between items-center">
                      <div className="font-mono">
                        <span className="text-[8px] text-slate-500 uppercase tracking-widest block font-bold">DEPLOYMENT FEE</span>
                        <span className="text-lg font-black text-white">
                          {trainingMatrix[activeBlock].deposit}
                        </span>
                      </div>
                      <button 
                        onClick={handleInitializeTraining}
                        className="px-4 py-2 bg-[#f59e0b] hover:bg-amber-400 text-black font-mono text-[9px] font-black uppercase tracking-widest rounded-lg transition-all shadow-[0_0_15px_rgba(245,158,11,0.15)] hover:shadow-[0_0_20px_rgba(245,158,11,0.35)] flex items-center justify-center gap-1.5 cursor-pointer"
                      >
                        <span>Provision Team</span>
                        <PlayCircle className="w-4 h-4 fill-black text-black" />
                      </button>
                    </div>
                    
                    <button
                      onClick={handleLaunchSandbox}
                      className="w-full py-2 border border-slate-700 bg-slate-900/30 hover:bg-slate-900 hover:border-indigo-500 text-indigo-400 hover:text-white transition-all text-[8.5px] tracking-widest font-mono uppercase font-black rounded-lg text-center flex items-center justify-center gap-2"
                    >
                      <Terminal className="w-3.5 h-3.5" />
                      <span>Enter Interactive Training Sandbox</span>
                    </button>
                  </div>
                </>
              )}

            </div>
          </motion.div>
          ) : activeMode === 'EXPLORER' ? (
            /* 1-to-100 Category Database Explorer (CAT-90 Core) */
            <motion.div 
              key="categories-explorer"
              className="grid grid-cols-1 lg:grid-cols-12 gap-6 relative z-10 flex-1 w-full"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.25 }}
            >
              {/* Left Column (Grid Span 4): Search & Categories Index */}
              <div className="lg:col-span-4 flex flex-col space-y-4">
                <div className="space-y-2">
                  <span className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest px-2">
                    Search Categories Engine
                  </span>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 w-4 h-4 text-slate-550 mr-1 text-slate-500" />
                    <input
                      type="text"
                      value={explorerSearch}
                      onChange={(e) => setExplorerSearch(e.target.value)}
                      placeholder="Search titles or subnodes..."
                      className="w-full bg-slate-957 border border-slate-900 rounded-xl py-2.5 pl-10 pr-4 font-mono text-xs text-white outline-none focus:border-indigo-500 transition-all placeholder-slate-700 bg-slate-950"
                    />
                  </div>
                </div>

                <div className="space-y-2 flex-1 flex flex-col min-h-0">
                  <span className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest px-2">
                    Categories Index (1 to 101)
                  </span>
                  <div className="space-y-1.5 overflow-y-auto max-h-[50-vh] lg:max-h-[55vh] pr-1 flex-1">
                    {CATEGORIES.filter(cat => {
                      if (!explorerSearch.trim()) return true;
                      const term = explorerSearch.toLowerCase();
                      return cat.title.toLowerCase().includes(term) || cat.nodes.some(node => node.toLowerCase().includes(term));
                    }).map((cat) => {
                      const isSelected = selectedExplorerCatId === cat.id;
                      return (
                        <button
                          key={cat.id}
                          onClick={() => setSelectedExplorerCatId(cat.id)}
                          className={`w-full text-left p-3.5 rounded-xl border transition-all duration-200 flex items-center justify-between font-mono text-xs ${
                            isSelected
                              ? 'border-[#f59e0b] bg-slate-950 text-white font-black shadow-[0_0_15px_rgba(245,158,11,0.06)]'
                              : 'border-slate-900 text-slate-400 bg-black/40 hover:border-slate-800 hover:text-slate-200'
                          }`}
                        >
                          <div className="flex items-center gap-2 truncate">
                            <span className="text-[#f59e0b] font-black w-8 shrink-0">#{cat.id}</span>
                            <span className="truncate tracking-wide text-slate-200">{cat.title}</span>
                          </div>
                          <span className="text-[8.5px] px-1.5 py-0.5 rounded bg-black border border-slate-900 text-slate-500 ml-1 font-bold">
                            {cat.nodes.length}
                          </span>
                        </button>
                      );
                    }).slice(0, 101)}
                  </div>
                </div>
              </div>

              {/* Right Column (Grid Span 8): Subcategory Listing & Operational Automation */}
              <div className="lg:col-span-8">
                {(() => {
                  const selectedCat = CATEGORIES.find(c => c.id === selectedExplorerCatId) || CATEGORIES[89];
                  return (
                    <div className="bg-gradient-to-br from-slate-950 to-black border border-slate-900 rounded-3xl p-6 flex flex-col justify-between h-full min-h-[480px]">
                      <div className="space-y-6">
                        {/* Title metadata strip */}
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-4 border-b border-slate-900">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono font-black text-amber-400 bg-amber-950/40 px-2.5 py-1 rounded border border-amber-900/50">
                                CATEGORY {selectedCat.id}
                              </span>
                              {selectedCat.protocol && (
                                <span className="text-[8px] font-mono text-indigo-400 bg-indigo-950/20 px-2 py-0.5 rounded border border-indigo-900/40 uppercase tracking-widest font-extrabold">
                                  Standard Protocol Core
                                </span>
                              )}
                            </div>
                            <h2 className="text-base font-sans font-black text-white mt-2 uppercase tracking-wider">
                              {selectedCat.title}
                            </h2>
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setAutomationCategory(selectedCat.title);
                                setAutomationSubNode(selectedCat.nodes[0] || "Default Channel");
                                setLingTab('automator');
                                if (typeof (window as any).showCosmicToast === 'function') {
                                  (window as any).showCosmicToast(`Loaded Category ${selectedCat.id} to Automator.`, "success");
                                } else {
                                  console.log(`Loaded Category ${selectedCat.id} to Automator.`);
                                }
                              }}
                              className="px-3 py-1.5 bg-indigo-950/40 hover:bg-indigo-900/50 border border-indigo-700/50 hover:border-indigo-500 text-indigo-300 font-mono text-[9px] font-black uppercase tracking-widest rounded-lg transition-all flex items-center gap-1.5 cursor-pointer"
                            >
                              <Cpu className="w-3.5 h-3.5 text-indigo-400 animate-pulse" />
                              <span>Automate Category</span>
                            </button>
                            <button
                              onClick={() => {
                                if (typeof (window as any).showCosmicToast === 'function') {
                                  (window as any).showCosmicToast(`UK Gov compliance audit verified for Node-${selectedCat.id}. Status: OK`, "success");
                                } else {
                                  alert(`UK Gov compliance audit verified for Node-${selectedCat.id}. Current Status: SECURE SYSTEM.`);
                                }
                              }}
                              className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 border border-slate-800 hover:border-slate-700 text-slate-300 font-mono text-[9px] font-black uppercase tracking-widest rounded-lg transition-all flex items-center gap-1.5 cursor-pointer"
                            >
                              <Scale className="w-3.5 h-3.5 text-slate-400" />
                              <span>Statutory Audit</span>
                            </button>
                          </div>
                        </div>

                        {selectedCat.protocol && (
                          <div className="p-4 bg-indigo-950/20 border border-indigo-900/30 rounded-xl space-y-1">
                            <span className="text-[8px] font-mono text-indigo-400 font-extrabold uppercase tracking-widest block">Operational Rule Vector:</span>
                            <p className="text-[10px] font-sans text-slate-400 leading-relaxed">
                              {selectedCat.protocol}
                            </p>
                          </div>
                        )}

                        {/* List of subnodes with custom numbering index */}
                        <div className="space-y-2.5">
                          <span className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-widest block px-1">
                            Sub-category Node List ({selectedCat.nodes.length})
                          </span>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[220px] overflow-y-auto pr-1">
                            {selectedCat.nodes.map((nodeName, nodeIdx) => (
                              <div
                                key={nodeIdx}
                                className="p-3 bg-[#050508] border border-slate-900 rounded-xl flex items-start gap-2.5 hover:border-slate-800 transition-all text-xs font-mono"
                              >
                                <span className="text-[10px] font-mono font-black text-[#f59e0b] shrink-0 w-6">
                                  {(nodeIdx + 1).toString().padStart(2, '0')}
                                </span>
                                <span className="text-[10px] text-slate-300 leading-relaxed select-all">
                                  {nodeName}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Subnode interaction block for CAT-90: NexoraX Linguistics Node */}
                        {selectedCat.id === 90 && (
                          <div className="border-t border-slate-900 pt-5 mt-5 space-y-4">
                            <div className="flex items-center justify-between">
                              <h3 className="text-xs font-mono font-black text-[#f59e0b] uppercase tracking-wider flex items-center gap-1.5">
                                <Languages className="w-4 h-4 text-[#f59e0b]" />
                                <span>Linguistics Hub & Dialogue Core</span>
                              </h3>
                              
                              <div className="flex gap-1 bg-black border border-slate-900 rounded-lg p-1">
                                <button
                                  onClick={() => setLingTab('tutor')}
                                  className={`px-2 py-1 font-mono text-[8px] uppercase font-bold tracking-widest rounded transition-all ${
                                    lingTab === 'tutor'
                                      ? 'bg-amber-500 text-black font-extrabold'
                                      : 'text-slate-500 hover:text-slate-300'
                                  }`}
                                >
                                  English Tutor
                                </button>
                                <button
                                  onClick={() => setLingTab('automator')}
                                  className={`px-2 py-1 font-mono text-[8px] uppercase font-bold tracking-widest rounded transition-all ${
                                    lingTab === 'automator'
                                      ? 'bg-amber-500 text-black font-extrabold'
                                      : 'text-slate-500 hover:text-slate-300'
                                  }`}
                                >
                                  Babel Automator
                                </button>
                              </div>
                            </div>

                            {lingTab === 'tutor' ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-black/80 border border-slate-900 rounded-2xl p-4 flex flex-col justify-between h-[220px]">
                                  <div className="overflow-y-auto space-y-3 pr-1 flex-1 text-[9px] font-mono">
                                    {lingChatHistory.map((m, idx) => (
                                      <div key={idx} className={`flex flex-col ${m.role === 'user' ? 'items-end' : 'items-start'}`}>
                                        <span className={`text-[7px] text-slate-500 mb-0.5 uppercase font-bold ${m.role === 'user' ? 'text-indigo-400' : 'text-amber-500'}`}>
                                          {m.role === 'user' ? 'NexoraX Administrator' : 'Expert IELTS Tutor'}
                                        </span>
                                        <div className={`p-2 rounded-xl max-w-[85%] leading-relaxed ${
                                          m.role === 'user'
                                            ? 'bg-indigo-950/30 border border-indigo-900/30 text-slate-200'
                                            : 'bg-zinc-900/60 border border-zinc-800 text-slate-300'
                                        }`}>
                                          {m.text}
                                        </div>
                                      </div>
                                    ))}
                                    {isLingResponding && (
                                      <div className="text-slate-600 animate-pulse text-[8px]">Analyzing dialect acoustic vector...</div>
                                    )}
                                  </div>

                                  <div className="flex gap-1.5 mt-2 border-t border-slate-900 pt-2">
                                    <input
                                      type="text"
                                      value={lingChatInput}
                                      onChange={(e) => setLingChatInput(e.target.value)}
                                      onKeyDown={(e) => { if (e.key === 'Enter') handleLingChatSend(); }}
                                      placeholder="Ask about IELTS levels..."
                                      className="flex-1 bg-[#050508] border border-slate-900 rounded-lg p-2 font-mono text-[9px] text-white outline-none focus:border-amber-500"
                                    />
                                    <button
                                      onClick={handleLingChatSend}
                                      disabled={isLingResponding}
                                      className="p-2 bg-[#f59e0b] hover:bg-amber-400 text-black rounded-lg transition-all flex justify-center items-center cursor-pointer"
                                    >
                                      <Send className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </div>

                                <div className="bg-[#050508] border border-slate-900 rounded-2xl p-4 flex flex-col justify-between h-[220px]">
                                  <div className="space-y-3">
                                    <span className="text-[8px] font-mono text-slate-500 uppercase tracking-widest block font-bold">Fife Dialect Translator Board</span>
                                    <p className="text-[9px] font-sans text-slate-400 leading-normal">
                                      Practise Scots idioms below. Try <strong className="text-slate-200">braw</strong>, <strong className="text-slate-200">bairn</strong>, <strong className="text-slate-200">crabbit</strong>, <strong className="text-slate-200 font-medium">dinnae</strong> to test translation explanations:
                                    </p>

                                    <input
                                      type="text"
                                      value={fifeInput}
                                      onChange={(e) => {
                                        setFifeInput(e.target.value);
                                        translateFifeToEnglish(e.target.value);
                                      }}
                                      placeholder="Enter Scots or Fife word..."
                                      className="w-full bg-black border border-slate-900 focus:border-[#f59e0b] rounded-lg p-2 font-mono text-xs text-white outline-none"
                                    />
                                  </div>

                                  {fifeOutput && (
                                    <div className="bg-slate-900/60 border border-slate-900 p-2.5 rounded-xl text-[8.5px] font-mono text-slate-300 mt-2 flex-1 overflow-y-auto whitespace-pre-wrap leading-relaxed">
                                      {fifeOutput}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              /* Dynamic Automator Controller Panel */
                              <div className="bg-black/80 border border-slate-900 rounded-2xl p-4 space-y-3">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                  <div className="space-y-1">
                                    <label className="text-[7.5px] font-mono text-slate-500 uppercase font-black">Target Node (Dynamic Category)</label>
                                    <input
                                      type="text"
                                      value={automationCategory}
                                      onChange={(e) => setAutomationCategory(e.target.value)}
                                      className="w-full bg-[#050508] border border-slate-900 rounded-lg p-2 font-mono text-[10px] text-white outline-none focus:border-indigo-500"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="text-[7.5px] font-mono text-slate-500 uppercase font-black">Sub-Node Endpoint</label>
                                    <input
                                      type="text"
                                      value={automationSubNode}
                                      onChange={(e) => setAutomationSubNode(e.target.value)}
                                      className="w-full bg-[#050508] border border-slate-900 rounded-lg p-2 font-mono text-[10px] text-white outline-none focus:border-indigo-500"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="text-[7.5px] font-mono text-slate-500 uppercase font-black">Lead/Customer Name</label>
                                    <input
                                      type="text"
                                      value={automationCustomerName}
                                      onChange={(e) => setAutomationCustomerName(e.target.value)}
                                      className="w-full bg-[#050508] border border-slate-905 rounded-lg p-2 font-mono text-[10px] text-white outline-none focus:border-indigo-500"
                                    />
                                  </div>
                                </div>

                                <div className="flex flex-col md:flex-row gap-3 pt-1">
                                  <div className="flex-1 flex flex-col justify-between">
                                    <button
                                      onClick={runAutomationController}
                                      disabled={automationStatus === 'running'}
                                      className="w-full py-2 bg-[#f59e0b] hover:bg-amber-400 text-black font-mono text-[8.5px] font-black uppercase tracking-widest rounded-lg transition-all font-bold flex items-center justify-center gap-1.5 cursor-pointer"
                                    >
                                      <Cpu className={`w-3 h-3 ${automationStatus === 'running' ? 'animate-spin' : ''}`} />
                                      <span>{automationStatus === 'running' ? 'Executing Workflow...' : 'Execute Automated Node-90 Controller'}</span>
                                    </button>

                                    <div className="bg-slate-950 border border-slate-910 rounded-xl p-2.5 h-24 font-mono text-[8px] text-[#f59e0b] overflow-y-auto space-y-1 mt-2 flex-1 bg-slate-950">
                                      {automationLogs.length === 0 ? (
                                        <div className="text-slate-700 italic">Automator idle. Waiting for trigger deployment...</div>
                                      ) : (
                                        automationLogs.map((log, idx) => (
                                          <div key={idx} className="leading-snug">{log}</div>
                                        ))
                                      )}
                                    </div>
                                  </div>

                                  <div className="flex-1 bg-[#050508] border border-slate-900 rounded-xl p-2.5 flex flex-col justify-between h-36 md:h-auto min-h-[140px]">
                                    <div>
                                      <span className="text-[7px] font-mono text-slate-500 uppercase tracking-widest block mb-1">MAPPED CORRESPONDENCE DRAFT:</span>
                                      {automationEmailDraft ? (
                                        <div className="text-[9.5px] font-mono text-slate-300 leading-relaxed select-all select-text max-h-[80px] overflow-y-auto whitespace-pre-wrap">
                                          {automationEmailDraft}
                                        </div>
                                      ) : (
                                        <div className="text-[8.5px] font-mono text-slate-650 italic">Draft uncompiled. Deploy node config to draft secure correspondence.</div>
                                      )}
                                    </div>
                                    <span className="text-[7px] font-mono text-slate-655 block pt-1 border-t border-slate-900 mt-1">
                                      LEVY: 1% PERPETUAL ZAKAT | TAX: 20% | NET SETTLE: Secure Payment Provider
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Footer signatures */}
                      <div className="border-t border-slate-900 pt-3.5 mt-5 flex justify-between items-center text-[7px] font-mono text-slate-500 uppercase tracking-wider font-extrabold pb-1">
                        <span>Lead Architect: Lead Director</span>
                        <span>Mirrored to NexoraX Resource Library via CloudSync-02</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </motion.div>
          ) : activeMode === 'LIST' ? (
            /* Beautiful 1-to-100 Fully Formatted List with Search, Filter & Copy Functions */
            <motion.div
              key="categories-registry-list"
              className="flex flex-col space-y-5 relative z-10 flex-1 w-full"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.25 }}
            >
              {/* Controls bar */}
              <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-4 bg-slate-950/60 border border-slate-900 rounded-2xl p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-rose-950/30 border border-rose-900/50 rounded-xl">
                    <BookOpen className="w-5 h-5 text-rose-400" />
                  </div>
                  <div>
                    <span className="text-[8px] font-mono font-black text-rose-500 uppercase tracking-widest block">NEXORAX ARCHITECT CORE</span>
                    <h2 className="text-sm font-sans font-black text-white uppercase tracking-wider">
                      NexoraX 1-to-101 Category Registry
                    </h2>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] font-mono text-slate-500 bg-black/40 border border-slate-900 px-3 py-1.5 rounded-lg flex items-center gap-1.5">
                    <Layers className="w-3.5 h-3.5 text-[#f59e0b]" />
                    Total Sub-Categories: <strong className="text-slate-200">{CATEGORIES.slice(0, 101).reduce((acc, cat) => acc + cat.nodes.length, 0)}</strong>
                  </span>
                  
                  <button
                    onClick={() => {
                      const fullText = generateCategoryList().join('\n');
                      navigator.clipboard.writeText(fullText);
                      if (typeof (window as any).showCosmicToast === 'function') {
                        (window as any).showCosmicToast("Complete 1-to-100 Category Registry copied to clipboard.", "success");
                      } else {
                        alert("Complete 1-to-100 Category Registry copied to clipboard successfully.");
                      }
                    }}
                    className="px-3.5 py-1.5 bg-[#f59e0b] hover:bg-amber-400 text-black font-mono text-[9px] font-black uppercase tracking-widest rounded-lg transition-all flex items-center gap-1.5 cursor-pointer font-bold"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 text-black" />
                    <span>Copy Full Text</span>
                  </button>

                  <button
                    onClick={() => {
                      const fullText = generateCategoryList().join('\n');
                      const blob = new Blob([`In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.\n\n${fullText}`], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'NEXORAX_NEXORAX_REGISTRY_1_TO_100.txt';
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                      if (typeof (window as any).showCosmicToast === 'function') {
                        (window as any).showCosmicToast("Successfully downloaded RAW plain-text ledger.", "success");
                      }
                    }}
                    className="px-3.5 py-1.5 bg-slate-900 hover:bg-slate-800 border border-slate-800 hover:border-slate-700 text-slate-300 font-mono text-[9px] font-black uppercase tracking-widest rounded-lg transition-all flex items-center gap-1.5 cursor-pointer"
                  >
                    <FileText className="w-3.5 h-3.5 text-slate-400" />
                    <span>Download Ledger</span>
                  </button>
                </div>
              </div>

              {/* Live search input specifically for registry view */}
              <div className="relative">
                <Search className="absolute left-3 top-3.5 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  value={explorerSearch}
                  onChange={(e) => setExplorerSearch(e.target.value)}
                  placeholder="Filter 1-to-100 formatted string nodes (e.g. search 'AI SYSTEMS' or 'Fife' or 'Category 61')..."
                  className="w-full bg-slate-950/60 border border-slate-900 focus:border-rose-500 rounded-xl py-3 pl-10 pr-4 font-mono text-xs text-white outline-none transition-all placeholder-slate-700"
                />
              </div>

              {/* Dynamic rendering of array of strings produced by generateCategoryList */}
              <div className="space-y-3 overflow-y-auto max-h-[550px] pr-2 scrollbar-thin scrollbar-thumb-slate-800">
                {(() => {
                  const items = generateCategoryList();
                  const filteredItems = items.filter(item => {
                    if (!explorerSearch.trim()) return true;
                    return item.toLowerCase().includes(explorerSearch.toLowerCase());
                  });
                  
                  if (filteredItems.length === 0) {
                    return (
                      <div className="text-center p-12 border border-slate-900 rounded-3xl bg-[#030305] text-slate-500 font-mono text-xs">
                        No entries match your filtering query in the system directory.
                      </div>
                    );
                  }

                  return filteredItems.map((item, index) => {
                    return (
                      <div key={index} className="bg-slate-950/80 border border-slate-900 rounded-2xl p-4 hover:border-slate-800 transition-all">
                        <pre className="font-mono text-[10.5px] leading-relaxed text-slate-300 whitespace-pre-wrap select-all">
                          {item}
                        </pre>
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Footer signature line */}
              <div className="flex justify-between items-center text-[7.5px] font-mono text-slate-500 uppercase tracking-widest pt-2 border-t border-slate-900">
                <span>SYSTEM CORE: SECURELY AUDITED</span>
                <span>MIRRORED TO NexoraX Resource Library VIA CloudSync-02</span>
              </div>
            </motion.div>
          ) : activeMode === 'DOMINION' ? (
            /* Beautiful Dominion Harvest & Staff board */
            <motion.div
              key="dominion-harvest-staff"
              className="flex flex-col space-y-6 relative z-10 flex-1 w-full text-slate-200"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.25 }}
            >
              {/* Islamic Preamble Banner */}
              <div className="bg-gradient-to-r from-emerald-950/40 via-slate-950/90 to-emerald-950/40 border border-emerald-900/30 rounded-2xl p-4 text-center shadow-lg">
                <span className="text-[10px] font-sans font-black text-emerald-400 block tracking-[0.2em] mb-1">
                  BISMILLAHIR RAHMANIR RAHIM
                </span>
                <p className="font-serif text-xs italic text-emerald-100 font-medium tracking-wide">
                  "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds."
                </p>
              </div>

              {/* Title Header with NexoraX Attributes */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-slate-950/60 border border-slate-900 rounded-2xl p-4 flex items-center gap-3">
                  <div className="p-2.5 bg-emerald-950/30 border border-emerald-900/50 rounded-xl shrink-0">
                    <Layers className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <span className="text-[7px] font-mono font-black text-emerald-500 uppercase tracking-widest block">MANDATED INFRASTRUCTURE</span>
                    <h2 className="text-xs font-sans font-black text-white uppercase tracking-wider">
                      11 HARVEST SECTORS
                    </h2>
                  </div>
                </div>

                <div className="bg-slate-950/60 border border-slate-900 rounded-2xl p-4 flex items-center gap-3">
                  <div className="p-2.5 bg-emerald-950/30 border border-emerald-900/50 rounded-xl shrink-0">
                    <User className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <span className="text-[7px] font-mono font-black text-emerald-500 uppercase tracking-widest block">STAFF ENTERPRISE STANDARDS MATCH</span>
                    <h2 className="text-xs font-sans font-black text-white uppercase tracking-wider">
                      Staff Coordinator & Communications Associate SYNC
                    </h2>
                  </div>
                </div>

                <div className="bg-slate-950/60 border border-slate-900 rounded-2xl p-4 flex items-center gap-3">
                  <div className="p-2.5 bg-emerald-950/30 border border-emerald-900/50 rounded-xl shrink-0">
                    <Shield className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <span className="text-[7px] font-mono font-black text-emerald-500 uppercase tracking-widest block">COMPLIANCE EDGE</span>
                    <h2 className="text-xs font-sans font-black text-white uppercase tracking-wider">
                      UK GOV / Secure Payment Provider PROTOCOLS
                    </h2>
                  </div>
                </div>
              </div>

              {/* Two Panel Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
                
                {/* Left Column: Script Console & Harvester Control (Grid 7) */}
                <div className="lg:col-span-7 flex flex-col space-y-5">
                  
                  {/* Harvester Config & Script Runner */}
                  <div className="bg-slate-950/80 border border-slate-900 rounded-3xl p-5 space-y-4 shadow-xl flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-center pb-3 border-b border-slate-900/80 mb-3">
                        <div className="flex items-center gap-2">
                          <Terminal className="w-4 h-4 text-emerald-400" />
                          <span className="text-xs font-mono font-bold tracking-wider text-slate-200">DOMINION HARVEST CONTROLLER</span>
                        </div>
                        <span className="text-[8px] font-mono font-bold bg-emerald-950/40 text-emerald-400 border border-emerald-900/40 px-2.5 py-0.5 rounded">
                          AI STUDY HARVEST SCRIPTS
                        </span>
                      </div>

                      {/* Code editor visualization */}
                      <div className="bg-[#030305] border border-slate-900 rounded-xl p-3 font-mono text-[10px] leading-relaxed text-slate-400 mb-4 select-text max-h-[140px] overflow-y-auto">
                        <div className="text-emerald-500">// NexoraX Automation Script: Dominion Harvest & Staff Integration</div>
                        <div className="text-slate-500">const harvestNodes = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];</div>
                        <div className="text-slate-500">const staffNodes = [21];</div>
                        <div className="text-purple-400">const<span className="text-slate-200"> broadcastPayload</span> = &#123;</div>
                        <div className="pl-4">url: <span className="text-amber-400">"{harvestPayload.url}"</span>,</div>
                        <div className="pl-4">offer: <span className="text-amber-400">"{harvestPayload.offer}"</span>,</div>
                        <div className="pl-4">target: <span className="text-amber-400">"{harvestPayload.target}"</span></div>
                        <div>&#125;;</div>
                        <br />
                        <div className="text-blue-400">function<span className="text-amber-300"> executeDominionHarvest</span>(nodes) &#123;</div>
                        <div className="pl-4 text-slate-400">console.log(<span className="text-amber-400">"Initiating Global Dominion Harvest..."</span>);</div>
                        <div className="text-blue-400">&#125;</div>
                      </div>

                      {/* Live Input Controls */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                        <div>
                          <label className="text-[8px] font-mono font-bold text-slate-500 uppercase tracking-wider block mb-1">Target Host Name</label>
                          <input
                            type="text"
                            value={harvestPayload.url}
                            onChange={(e) => setHarvestPayload(prev => ({ ...prev, url: e.target.value }))}
                            className="w-full bg-black/50 border border-slate-900 focus:border-emerald-500 rounded-lg p-2 font-mono text-[10px] text-white outline-none transition-all"
                          />
                        </div>
                        <div>
                          <label className="text-[8px] font-mono font-bold text-slate-500 uppercase tracking-wider block mb-1">Broadcast Offer Title</label>
                          <input
                            type="text"
                            value={harvestPayload.offer}
                            onChange={(e) => setHarvestPayload(prev => ({ ...prev, offer: e.target.value }))}
                            className="w-full bg-black/50 border border-slate-900 focus:border-emerald-500 rounded-lg p-2 font-mono text-[10px] text-white outline-none transition-all"
                          />
                        </div>
                        <div>
                          <label className="text-[8px] font-mono font-bold text-slate-500 uppercase tracking-wider block mb-1">Target Geo Node</label>
                          <select
                            value={harvestPayload.target}
                            onChange={(e) => setHarvestPayload(prev => ({ ...prev, target: e.target.value }))}
                            className="w-full bg-black/50 border border-slate-900 focus:border-emerald-500 rounded-lg p-2 font-mono text-[10px] text-white outline-none transition-all"
                          >
                            <option value="Global">Global</option>
                            <option value="United Kingdom">United Kingdom</option>
                            <option value="Europe Central">Europe Central</option>
                            <option value="Pakistan Command">Pakistan Command</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Harvest Execute Button */}
                    <button
                      onClick={handleExecuteHarvest}
                      disabled={isHarvesting}
                      className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 text-black font-mono text-[9px] font-black uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer border-none shadow-lg shadow-emerald-950/20 font-bold"
                    >
                      {isHarvesting ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin text-black" />
                          <span>Executing Global outreach Sequences...</span>
                        </>
                      ) : (
                        <>
                          <PlayCircle className="w-4 h-4 fill-black text-black" />
                          <span>Run Dominion Harvest Protocol</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Terminal Output */}
                  <div className="bg-[#030305] border border-slate-900 rounded-3xl p-5 space-y-3">
                    <span className="text-[8px] font-mono font-bold text-slate-500 uppercase tracking-widest block">
                      SYSTEM INTEGRATION LOG (SANDBOX TERMINAL):
                    </span>
                    <div className="font-mono text-[9.5px] leading-relaxed text-emerald-400 bg-black/40 border border-slate-950 rounded-xl p-4 h-[150px] overflow-y-auto space-y-1.5 scrollbar-thin scrollbar-thumb-slate-900">
                      {harvestLogs.map((log, lIdx) => (
                        <div key={lIdx} className="whitespace-pre-wrap select-all">
                          {log}
                        </div>
                      ))}
                    </div>
                  </div>

                </div>

                {/* Right Column: Tax Protocol Calculator & Enterprise Standards Sync (Grid 5) */}
                <div className="lg:col-span-5 flex flex-col space-y-5">
                  
                  {/* Tiered Extraction Protocol Calculator */}
                  <div className="bg-slate-950/80 border border-slate-900 rounded-3xl p-5 space-y-4 shadow-xl">
                    <div className="flex items-center gap-2 pb-2 border-b border-slate-900/80">
                      <Calculator className="w-4 h-4 text-emerald-400" />
                      <span className="text-xs font-mono font-bold text-slate-200">TIERED EXTRACTION ENGINE</span>
                    </div>

                    <div className="space-y-3 font-mono">
                      <div>
                        <label className="text-[8px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Raw Daily Gross Harvest</label>
                        <div className="relative">
                          <span className="absolute left-3 top-2.5 text-xs text-slate-500">£</span>
                          <input
                            type="number"
                            value={harvestAmount}
                            onChange={(e) => setHarvestAmount(Number(e.target.value))}
                            className="w-full bg-black/50 border border-slate-900 focus:border-emerald-500 rounded-lg py-2 pl-7 pr-3 text-xs text-white outline-none transition-all"
                          />
                        </div>
                      </div>

                      {/* Calculations breakdown block */}
                      <div className="bg-black/40 border border-slate-900/60 rounded-xl p-3.5 space-y-2.5 text-[9.5px]">
                        <div className="flex justify-between items-center text-slate-400">
                          <span>1% 3% Community Allocation:</span>
                          <span className="font-extrabold text-[#f59e0b]">
                            -£{Number(calculateLevy()).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </span>
                        </div>
                        
                        <div className="pl-3.5 border-l border-amber-900/35 space-y-1 text-[8.5px] text-slate-500">
                          <div className="flex justify-between">
                            <span>├ Charity 1 (Dunfermline):</span>
                            <span>£{Number(calculateCharitySplit()).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>├ Charity 2 (Global):</span>
                            <span>£{Number(calculateCharitySplit()).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>└ Charity 3 (UK):</span>
                            <span>£{Number(calculateCharitySplit()).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>
                        </div>

                        <div className="flex justify-between items-center text-slate-400 border-t border-slate-900 pt-2">
                          <span>Remaining Balance:</span>
                          <span>£{Number(calculateRemainingBalance()).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                        </div>

                        <div className="flex justify-between items-center text-slate-400">
                          <span>10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee):</span>
                          <span className="font-extrabold text-[#f43f5e]">
                            -£{Number(calculateTax()).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </span>
                        </div>

                        <div className="flex flex-col border-t border-[#f59e0b]/20 pt-2 text-[#fcfaee] bg-[#f59e0b]/5 p-2 rounded-lg">
                          <div className="flex justify-between font-bold text-xs">
                            <span className="text-[#f59e0b]">Net Settle finance:</span>
                            <span className="text-[#10b981]">
                              £{Number(calculateNetSettle()).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                            </span>
                          </div>
                          <div className="text-[7.5px] text-slate-500 mt-1 uppercase tracking-widest text-right">
                            Sort Code: 04-**-** | Acc: ******15 (Secure Payment Provider)
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Nexorax Empire Hardware & Connectivity Sync */}
                  <div className="bg-slate-950/80 border border-slate-900 rounded-3xl p-5 space-y-4 shadow-xl">
                    <div className="flex items-center justify-between pb-2 border-b border-slate-900/80">
                      <div className="flex items-center gap-2">
                        <Network className="w-4 h-4 text-emerald-400" />
                        <span className="text-xs font-mono font-bold text-slate-200">HARDWARE & CONNECTIVITY SYNC</span>
                      </div>
                      <span className="text-[7px] font-mono font-black bg-emerald-950 px-2 py-0.5 rounded border border-emerald-900/60 text-emerald-400">
                        PEPLINK & STARLINK BONDING ACTIVE
                      </span>
                    </div>

                    {/* Python Script Specification Frame */}
                    <div className="bg-black/50 border border-slate-900 rounded-xl p-2.5 font-mono text-[8.5px] leading-relaxed text-slate-400">
                      <div className="text-emerald-500"># Python Command Sync Node-09.2</div>
                      <div><span className="text-purple-400">def</span> <span className="text-[#f59e0b]">optimize_ecosystem</span>():</div>
                      <div className="pl-3.5">devices = [<span className="text-amber-400">"Framework_Laptop"</span>, <span className="text-amber-400">"Workstation_PC"</span>, <span className="text-amber-400">"Mobile_Phone"</span>]</div>
                      <div className="pl-3.5"><span className="text-purple-400">for</span> device <span className="text-purple-400">in</span> devices:</div>
                      <div className="pl-7"><span className="text-cyan-400">print</span>(<span className="text-amber-400">f"Applying high-performance configuration..."</span>)</div>
                      <div className="pl-7 text-slate-500"># Integrate Peplink/Starlink network protocols</div>
                      <div className="pl-7 text-slate-500"># Deploy 10% Maintenance & Hosting Fee tracking module</div>
                    </div>

                    <div className="space-y-2.5">
                      {(Object.entries(deviceSyncStates) as [string, { connection: string; latency: string; optimized: boolean; band: string }][]).map(([device, data]) => (
                        <div key={device} className="bg-[#030305] border border-slate-900/80 rounded-xl p-3 flex justify-between items-center text-[10px]">
                          <div className="flex items-center gap-2.5">
                            <div className={`p-1.5 rounded-lg border ${data.optimized ? 'bg-emerald-950/20 border-emerald-900/50 text-emerald-400' : 'bg-slate-950 border-slate-900 text-slate-500'}`}>
                              <Cpu className="w-3.5 h-3.5 animate-pulse" />
                            </div>
                            <div>
                              <div className="font-sans font-bold text-slate-200">{device.replace('_', ' ')}</div>
                              <div className="text-[7.5px] font-mono text-slate-500 block">{data.connection} &bull; {data.band}</div>
                            </div>
                          </div>

                          <div className="text-right flex items-center gap-3">
                            <div>
                              <div className="text-[7.5px] font-mono text-slate-500 uppercase">LATENCY</div>
                              <div className={`font-mono text-[9px] font-bold ${data.optimized ? 'text-emerald-400' : 'text-slate-400'}`}>{data.latency}</div>
                            </div>
                            <div className={`text-[7.5px] font-mono font-black border px-2 py-0.5 rounded tracking-widest ${
                              data.optimized 
                                ? 'bg-emerald-950/30 border-emerald-900/65 text-emerald-400' 
                                : 'bg-slate-950/40 border-slate-900 text-slate-500 animate-pulse'
                            }`}>
                              {data.optimized ? "OPTIMIZED" : "STANDBY"}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={handleExecuteHardwareSync}
                      disabled={isSyncingDevices}
                      className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-950 disabled:text-indigo-700 text-white font-mono text-[8px] font-black uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer border-none shadow-lg shadow-indigo-950/20 font-bold"
                    >
                      {isSyncingDevices ? (
                        <>
                          <RefreshCw className="w-3.5 h-3.5 animate-spin text-white" />
                          <span>Executing optimize_ecosystem()...</span>
                        </>
                      ) : (
                        <>
                          <Zap className="w-3.5 h-3.5 fill-current text-white" />
                          <span>Optimize Connectivity Ecosystem</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Staff Compliance Sync & Secure Payment Provider Alignment */}
                  <div className="bg-slate-950/80 border border-slate-900 rounded-3xl p-5 space-y-4 shadow-xl flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 pb-2 border-b border-slate-900/80 mb-3">
                        <Briefcase className="w-4 h-4 text-emerald-400" />
                        <span className="text-xs font-mono font-bold text-slate-200">STAFF COMPLIANCE & TRAINING PORTAL</span>
                      </div>

                      <div className="space-y-3.5">
                        {(Object.entries(staffStatus) as [string, { status: string; ielts: string; progress: number; trainingNode: string }][]).map(([name, data]) => (
                          <div key={name} className="bg-black/30 border border-slate-900 rounded-xl p-3 space-y-2.5">
                            <div className="flex justify-between items-start">
                              <div>
                                <span className="text-[9.5px] font-mono font-black text-slate-200">{name.toUpperCase()}</span>
                                <span className="text-[7.5px] font-mono text-slate-500 block">{data.trainingNode}</span>
                              </div>
                              <span className={`text-[7px] font-mono font-bold px-2 py-0.5 rounded border uppercase tracking-wider ${
                                data.status === 'Pending-Migration'
                                  ? 'bg-amber-950/20 border-amber-900/50 text-amber-500'
                                  : 'bg-indigo-950/20 border-indigo-900/50 text-indigo-400'
                              }`}>
                                {data.status}
                              </span>
                            </div>

                            {/* Practice metrics */}
                            <div className="space-y-1">
                              <div className="flex justify-between text-[7.5px] font-mono text-slate-400">
                                <span>IELTS test Training progress:</span>
                                <span>{data.progress}%</span>
                              </div>
                              <div className="h-1.5 w-full bg-slate-950 rounded-full overflow-hidden">
                                <div className="h-full bg-emerald-500 transition-all duration-300 rounded-full" style={{ width: `${data.progress}%` }}></div>
                              </div>
                            </div>

                            <div className="flex justify-between items-center text-[8px] font-mono text-slate-500">
                              <span>Practice standard status:</span>
                              <span className="text-slate-300 font-bold">{data.ielts}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Control Sync buttons */}
                    <div className="grid grid-cols-2 gap-2 mt-4">
                      <button
                        onClick={() => {
                          setStaffStatus(prev => ({
                            ...prev,
                            "Staff Coordinator": { ...prev["Staff Coordinator"], progress: Math.min(100, prev["Staff Coordinator"].progress + 5) },
                            "Communications Associate Parween": { ...prev["Communications Associate Parween"], progress: Math.min(100, prev["Communications Associate Parween"].progress + 10) }
                          }));
                          if (typeof (window as any).showCosmicToast === 'function') {
                            (window as any).showCosmicToast("Associates competence updated successfully in training portal.", "success");
                          } else {
                            alert("Competence practice scores increased.");
                          }
                        }}
                        className="py-2.5 bg-slate-900 hover:bg-slate-800 border border-slate-800 hover:border-slate-700 text-slate-300 font-mono text-[8px] font-black uppercase tracking-wider rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer font-bold"
                      >
                        <BookOpen className="w-3.5 h-3.5 text-emerald-400" />
                        <span>Push Practice</span>
                      </button>
                      
                      <button
                        onClick={() => {
                          const invitationText = `In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.

Date: ${new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}

OFFICIAL HOME OFFICE SPONSORSHIP BRIEF: NexoraX Digital Ltd (SC887095)
Subject: Sponsorship Case File Sync (Vanguard Matrix)

Target associates: Staff Coordinator (Operations Node) and Communications Associate Parween (Communications Node)
Position Match: Principal Private Secretary / Operational Director Node

NECESSITY STATEMENT:
The operational stabilization of the proprietary Amazonia Grid and custom systems (CAT-84 NexoraX Infrastructure) relies strictly on immediate placement of selected staff.

All remuneration handles under a Zero-Fund scheme synced directly with Secure Payment Provider NI: NH******B.

Director Lead Director
NexoraX Digital Ltd (SC887095)
Secure Payment Provider Settle Hook Active`;
                          
                          navigator.clipboard.writeText(invitationText);
                          if (typeof (window as any).showCosmicToast === 'function') {
                            (window as any).showCosmicToast("Sponsorship case letter template copied successfully.", "success");
                          } else {
                            alert("Home office official letter template copied successfully!");
                          }
                        }}
                        className="py-2.5 bg-[#f59e0b] hover:bg-amber-400 text-black font-mono text-[8px] font-black uppercase tracking-wider rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer font-bold"
                      >
                        <FileText className="w-3.5 h-3.5" />
                        <span>HO Brief template</span>
                      </button>
                    </div>
                  </div>

                </div>

              </div>

              {/* Secure replication footer line */}
              <div className="flex justify-between items-center text-[7.5px] font-mono text-slate-500 uppercase tracking-widest pt-3 border-t border-slate-900 mt-2">
                <span>SYSTEM REPLICATION LINK: /var/www/nexorax_source &lt;-&gt; gdrive:Nexorax_Ecosystem_Backups/Source_Core_V2_LivingBrain | SECURED BY CloudSync-02</span>
                <span>COMMS FREQUENCY: support@nexoraxdigital.co.uk | 08000 622827</span>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="regulatory-inquiry"
              className="grid grid-cols-1 lg:grid-cols-12 gap-6 relative z-10 flex-1"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.25 }}
            >
              {/* Left Column (Grid Span 6): Interactive Advisor Input & Output */}
              <div className="lg:col-span-6 flex flex-col space-y-4">
                <div className="bg-[#050507]/90 border border-slate-900 rounded-3xl p-6 flex flex-col justify-between relative min-h-[480px]">
                  <div>
                    <div className="flex items-center justify-between border-b border-slate-900 pb-3 mb-4">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.2)]" />
                        <h2 className="font-mono text-[10px] uppercase font-black tracking-widest text-[#f8fafc]">NexoraX Advisor Console</h2>
                      </div>
                      <span className="text-[7px] font-mono text-[#06b6d4] uppercase bg-cyan-950/40 border border-cyan-800/30 px-2 py-0.5 rounded font-black">C-Grounding Active</span>
                    </div>

                    <div className="space-y-2 mb-4">
                      <label className="text-[8px] font-mono text-slate-500 uppercase tracking-wider font-extrabold block">Select Compliance Category Matrix:</label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {(['compliance', 'transport', 'corporate_gov', 'grants_funding'] as const).map((cat) => (
                          <button
                            key={cat}
                            onClick={() => {
                              setSelectedInquiryCat(cat);
                              setInquiryText(catPresets[cat]);
                            }}
                            className={`px-2 py-2.5 rounded-lg border font-mono text-[8px] uppercase tracking-wider transition-all cursor-pointer ${
                              selectedInquiryCat === cat
                                ? 'border-cyan-500 bg-cyan-950/35 text-[#22d3ee] font-black'
                                : 'border-slate-900 bg-black/40 text-slate-500 hover:border-slate-850 hover:text-slate-300'
                            }`}
                          >
                            {cat === 'corporate_gov' ? 'Corp Govt' : cat === 'grants_funding' ? 'Grants & Funding' : cat}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      <label className="text-[8px] font-mono text-slate-500 uppercase tracking-wider font-extrabold block">Regulatory Inquiry Text:</label>
                      <textarea
                        value={inquiryText}
                        onChange={(e) => setInquiryText(e.target.value)}
                        placeholder="State your compliance or regulatory inquiry..."
                        className="w-full h-24 bg-black border border-slate-900 rounded-xl p-3 font-mono text-[10px] text-slate-300 focus:outline-none focus:border-cyan-800/60 leading-relaxed placeholder-slate-700 resize-none"
                      />
                    </div>

                    <button
                      onClick={handleSendRegulatoryInquiry}
                      disabled={isInquiring}
                      className="w-full py-3 bg-[#06b6d4] hover:bg-cyan-400 disabled:bg-slate-950 text-black border border-cyan-900 font-mono text-[8px] font-black uppercase tracking-widest rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 font-bold"
                    >
                      {isInquiring ? (
                        <>
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          <span>Advising System Alignment...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-3.5 h-3.5" />
                          <span>Dispatch Inquiry to NexoraX Advisor</span>
                        </>
                      )}
                    </button>
                  </div>

                  {inquiryResult && (
                    <div className="border-t border-slate-900 pt-3 mt-4 flex items-center justify-between text-[7px] font-mono text-slate-500">
                      <span>STABILITY: STANDARD ONLINE</span>
                      <span>LATENCY: 0.8s SLA ACHIEVED</span>
                    </div>
                  )}
                </div>

                {/* Continuous Compliance Academy Card */}
                <div className="bg-[#050507]/90 border border-[#f59e0b]/20 hover:border-[#f59e0b]/45 rounded-3xl p-5 space-y-3 transition-all">
                  <div className="flex items-center gap-1.5 border-b border-white/5 pb-2">
                    <BookOpen className="w-3.5 h-3.5 text-amber-500" />
                    <span className="text-[9px] font-mono font-black text-slate-300 uppercase tracking-wider">Continuous Compliance Academy (NODE-101 Verification Tracks)</span>
                  </div>
                  <p className="text-[8px] font-sans text-slate-500 leading-normal font-semibold">
                    Select a core specialized NexoraX curriculum below to initiate a live interactive regulatory training session. The neural node will fetch latest UK precedents and immediately compile a customized evaluation quiz.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {advancedTracks.map((track) => {
                      const isActive = inquiryText === track.prompt;
                      return (
                        <button
                          key={track.id}
                          onClick={() => handleTriggerAdvancedLearningTrack(track)}
                          disabled={isInquiring || isCompilingQuiz}
                          className={`p-3 rounded-xl border text-left flex flex-col gap-1 transition-all cursor-pointer ${
                            isActive
                              ? 'border-amber-500 bg-amber-950/25 text-white shadow-[0_0_15px_rgba(245,158,11,0.08)]'
                              : 'border-slate-900 bg-slate-950/40 text-slate-400 hover:border-slate-800 hover:bg-slate-950 hover:text-slate-200'
                          }`}
                        >
                          <span className="text-[8.5px] font-sans font-extrabold text-[#f59e0b]">{track.title}</span>
                          <span className="text-[7.5px] font-mono text-slate-500 font-semibold line-clamp-1 leading-normal">
                            {track.category.toUpperCase()} • {track.prompt}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* NexoraX Compliance Progression & Knowledge Depth Meter */}
                <div className="bg-[#050507]/90 border border-indigo-900/40 hover:border-indigo-800/60 rounded-3xl p-5 space-y-4 transition-all">
                  <div className="flex items-center justify-between border-b border-white/5 pb-2">
                    <div className="flex items-center gap-1.5">
                      <TrendingUp className="w-3.5 h-3.5 text-indigo-400" />
                      <span className="text-[9px] font-mono font-black text-slate-200 uppercase tracking-wider">NexoraX Compliance Progression</span>
                    </div>
                    <span className="text-[7.5px] font-mono text-emerald-400 bg-emerald-950/35 border border-emerald-900/40 px-2.5 py-0.5 rounded uppercase font-bold">Active Engine</span>
                  </div>

                  {/* Knowledge Depth Meter Visualization */}
                  <div className="bg-black/50 border border-slate-900/60 p-4 rounded-2xl flex flex-col sm:flex-row gap-4 items-center">
                    {/* SVG Radial Arc Depth Indicator */}
                    <div className="relative w-16 h-16 flex items-center justify-center shrink-0">
                      <svg className="w-full h-full transform -rotate-90">
                        <circle
                          cx="32"
                          cy="32"
                          r="28"
                          className="stroke-slate-900"
                          strokeWidth="4.5"
                          fill="transparent"
                        />
                        <motion.circle
                          cx="32"
                          cy="32"
                          r="28"
                          className="stroke-indigo-500"
                          strokeWidth="4.5"
                          fill="transparent"
                          strokeDasharray="175.92"
                          initial={{ strokeDashoffset: 175.92 }}
                          animate={{ strokeDashoffset: 175.92 - (175.92 * Math.max(0, Math.min(progression.excellentCount, 4))) / 4 }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-[11px] font-mono font-black text-white">{progression.excellentCount * 25}%</span>
                        <span className="text-[6px] font-mono text-slate-500 uppercase font-bold">DEPTH</span>
                      </div>
                    </div>

                    {/* Rank Badge Visualization */}
                    {(() => {
                      const badge = getRankBadgeInfo(progression.excellentCount);
                      return (
                        <div className={`px-3.5 py-2.5 rounded-xl border ${badge.bgColor} flex items-center gap-2 shrink-0 md:max-w-[130px] w-full text-left transition-all`}>
                          <Award className={`w-4 h-4 ${badge.iconColor} shrink-0 animate-pulse`} />
                          <div className="truncate leading-none">
                            <span className="text-[6px] font-mono opacity-70 uppercase block font-bold">Badge Rank</span>
                            <span className="text-[8.5px] font-sans font-black tracking-wide block uppercase truncate mt-0.5">{badge.name}</span>
                          </div>
                        </div>
                      );
                    })()}

                    <div className="flex-1 space-y-1.5 w-full text-left">
                      <div>
                        <span className="text-[7.5px] font-mono text-indigo-400 block uppercase font-black leading-none tracking-wider">CURRENT COMPLIANCE RANK</span>
                        <span className="text-[9.5px] font-sans font-black text-[#f59e0b] leading-tight block uppercase mt-1">
                          {progression.excellentCount === 0 ? "Level 1: Trainee Compliance Associate" :
                           progression.excellentCount === 1 ? "Level 2: Verified Assessment Specialist" :
                           progression.excellentCount === 2 ? "Level 3: NexoraX Operations Director" :
                           progression.excellentCount === 3 ? "Level 4: NexoraX Regulatory Counsel" : 
                           "Level 5: Immutable General Counsel"}
                        </span>
                      </div>

                      {/* Performance rating meters */}
                      <div className="space-y-1">
                        <div className="flex justify-between text-[6.5px] font-mono text-slate-500">
                          <span>UNLOCKED KNOWLEDGE DOMAIN LEVEL PROGRESSION</span>
                          <span className="text-slate-350">{progression.excellentCount}/4 PERFECTS</span>
                        </div>
                        <div className="w-full bg-slate-950 h-1.5 rounded-full overflow-hidden border border-slate-900">
                          <motion.div 
                            className="bg-gradient-to-r from-indigo-500 to-amber-500 h-full rounded-full"
                            style={{ width: `${Math.min(100, (progression.excellentCount / 4) * 100)}%` }}
                            initial={{ width: 0 }}
                            animate={{ width: `${Math.min(100, (progression.excellentCount / 4) * 100)}%` }}
                            transition={{ duration: 0.5 }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Micro stats table */}
                  <div className="grid grid-cols-4 gap-1.5 text-center">
                    <div className="bg-slate-950 border border-slate-900 p-2 rounded-xl">
                      <span className="text-[6px] font-mono text-slate-500 block leading-none pb-1">TOTAL EXAMS</span>
                      <span className="text-[9.50px] font-mono font-black text-slate-200">{progression.totalAttempts}</span>
                    </div>
                    <div className="bg-slate-950 border border-slate-900 p-2 rounded-xl">
                      <span className="text-[6px] font-mono text-slate-500 block leading-none pb-1">PERFECTS</span>
                      <span className="text-[9.50px] font-mono font-black text-emerald-400">{progression.excellentCount}</span>
                    </div>
                    <div className="bg-slate-950 border border-slate-900 p-2 rounded-xl">
                      <span className="text-[6px] font-mono text-slate-500 block leading-none pb-1">ACCUM SCORE</span>
                      <span className="text-[9.50px] font-mono font-black text-amber-500">{progression.cumulativeScore}</span>
                    </div>
                    <div className="bg-slate-950 border border-slate-900 p-2 rounded-xl">
                      <span className="text-[6px] font-mono text-slate-500 block leading-none pb-1">UNLOCKED</span>
                      <span className="text-[9.50px] font-mono font-black text-cyan-400">{progression.unlockedCount}/100</span>
                    </div>
                  </div>

                  <div className="flex justify-between items-center text-[7px] font-mono text-slate-500">
                    <span>* Achieving "EXCELLENT" status (100% quiz accuracy) decrypts 20 more nodes</span>
                    <button 
                      onClick={() => {
                        if (confirm("Reset current compliance progression status?")) {
                          saveProgression({ excellentCount: 0, totalAttempts: 0, cumulativeScore: 0, unlockedCount: 20 });
                        }
                      }}
                      className="text-rose-500/70 hover:text-rose-400 underline transition-all font-bold cursor-pointer"
                    >
                      RESET STATS
                    </button>
                  </div>
                </div>

                {/* Node 101: Continuous Compliance Learning Directory */}
                <div className="bg-[#050507]/90 border border-slate-900 hover:border-slate-800 rounded-3xl p-5 space-y-3.5 transition-all">
                  <div className="flex items-center justify-between border-b border-white/5 pb-2">
                    <div className="flex items-center gap-1.5">
                      <Zap className="w-3.5 h-3.5 text-cyan-400 animate-pulse" />
                      <span className="text-[9px] font-mono font-black text-slate-300 uppercase tracking-wider">Node 101: Continuous Compliance Directory</span>
                    </div>
                    <span className="text-[7.5px] font-mono text-slate-500">Node-101 Verification Hub</span>
                  </div>

                  <p className="text-[8px] font-sans text-slate-500 leading-normal font-semibold">
                    Acquire elite structural knowledge across specific operational sub-nodes. Scroll or search within unlocked items below. Click "Study & Practise" to execute official AI advisor briefings.
                  </p>

                  <div className="space-y-2">
                    {/* Search & Stats bar */}
                    <div className="flex gap-2 items-center bg-black border border-slate-900 rounded-lg px-2 py-1.5">
                      <Search className="w-3.5 h-3.5 text-slate-600" />
                      <input
                        type="text"
                        placeholder="Search sub-nodes (e.g. sponsorship, levy, SecurePayment...)"
                        value={unlockedSubNodeSearch}
                        onChange={(e) => setUnlockedSubNodeSearch(e.target.value)}
                        className="bg-transparent border-none text-[8.5px] text-slate-300 placeholder-slate-600 focus:outline-none focus:ring-0 w-full font-mono shrink-0"
                      />
                    </div>

                    {/* Scrollable grid list */}
                    <div className="max-h-[170px] overflow-y-auto space-y-1.5 pr-1 text-slate-400">
                      {(() => {
                        const cat101 = CATEGORIES.find(c => c.id === 101);
                        const nodesList = cat101?.nodes || [];
                        const filtered = nodesList.map((name, index) => ({ name, index }))
                          .filter(item => {
                            if (!unlockedSubNodeSearch) return true;
                            return item.name.toLowerCase().includes(unlockedSubNodeSearch.toLowerCase());
                          });

                        if (filtered.length === 0) {
                          return <div className="text-center py-3 text-[8px] font-mono text-slate-600 uppercase">No matching sub-nodes detected.</div>;
                        }

                        return filtered.map(({ name, index }) => {
                          const isUnlocked = index < progression.unlockedCount;
                          return (
                            <div 
                              key={index} 
                              className={`p-2 rounded-xl border flex items-center justify-between gap-3 transition-all ${
                                isUnlocked 
                                  ? 'border-emerald-950/30 bg-emerald-950/10 text-slate-300' 
                                  : 'border-slate-950 bg-black/40 text-slate-600 select-none'
                              }`}
                            >
                              <div className="flex items-center gap-2 truncate">
                                {isUnlocked ? (
                                  <Unlock className="w-3 h-3 text-emerald-400 shrink-0" />
                                ) : (
                                  <Lock className="w-3 h-3 text-slate-700 shrink-0" />
                                )}
                                <div className="truncate text-left">
                                  <span className="text-[7.5px] font-mono font-black text-[#06b6d4] block leading-none">SUB-NODE {(index+1).toString().padStart(3, '0')}</span>
                                  <span className="text-[8.5px] font-sans font-extrabold truncate block">{name}</span>
                                </div>
                              </div>

                              {isUnlocked ? (
                                <button
                                  onClick={async () => {
                                    // Custom target-specific prompt representing that unlocked sub-node
                                    let subNodeCategory: 'compliance' | 'transport' | 'corporate_gov' = 'compliance';
                                    if (name.toLowerCase().includes('levy') || name.toLowerCase().includes('bank') || name.toLowerCase().includes('tax') || name.toLowerCase().includes('gavel')) {
                                      subNodeCategory = 'corporate_gov';
                                    } else if (name.toLowerCase().includes('work') || name.toLowerCase().includes('funding') || name.toLowerCase().includes('cost')) {
                                      subNodeCategory = 'transport';
                                    }

                                    const targetPrompt = `Analyze the structural mandates, verified guidelines and operational necessity of NexoraX Node 101 Sub-node: "${name}". Detail how it integrates with UK regulatory standards, Sponsorship licensing, and official Director directives.`;
                                    
                                    // Set state
                                    setSelectedInquiryCat(subNodeCategory);
                                    setInquiryText(targetPrompt);
                                    
                                    // Scroll context smoothly to advisor console view
                                    if (typeof (window as any).showCosmicToast === 'function') {
                                      (window as any).showCosmicToast(`Sub-node ${index+1} loaded. Dispatching live neural briefing query...`, "success");
                                    }

                                    // Auto query dispatch
                                    setIsInquiring(true);
                                    setInquiryResult(null);
                                    setQuizManifest(null);
                                    setEvaluationReport(null);
                                    setAnsweredQuestions({});
                                    try {
                                      const response = await fetch('/api/regulatory/inquire', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                          question: targetPrompt,
                                          category: subNodeCategory,
                                          timestamp: new Date().toISOString()
                                        })
                                      });
                                      if (response.ok) {
                                        const data = await response.json();
                                        setInquiryResult(data);
                                      }
                                    } catch (err) {
                                      console.error(err);
                                    } finally {
                                      setIsInquiring(false);
                                    }
                                  }}
                                  disabled={isInquiring}
                                  className="px-2.5 py-1 text-[7.5px] font-mono uppercase bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-900 text-white rounded transition-all cursor-pointer font-bold shrink-0"
                                >
                                  Study & Practise
                                </button>
                              ) : (
                                <span className="text-[6.5px] font-mono text-slate-700 uppercase uppercase tracking-wider font-extrabold shrink-0">LOCKED</span>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>

              </div>

              {/* Right Column (Grid Span 6): Advisor Output & Dynamic Quiz Manifest */}
              <div className="lg:col-span-6 flex flex-col space-y-4">
                {inquiryResult ? (
                  <div className="bg-gradient-to-br from-slate-950 to-[#030712] border border-slate-900 rounded-3xl p-6 relative overflow-hidden flex flex-col justify-between min-h-[190px]">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between border-b border-slate-900 pb-3">
                        <span className="text-[8px] font-mono text-cyan-400 font-black tracking-widest uppercase">Verified Response Dossier</span>
                        <Award className="w-4 h-4 text-[#f59e0b]" />
                      </div>
                      
                      <div className="font-sans text-[11px] text-slate-350 space-y-2 leading-relaxed bg-[#020205] border border-slate-950 p-4 rounded-2xl select-text max-h-[150px] overflow-y-auto font-medium">
                        <p className="text-white leading-relaxed">{inquiryResult.answer}</p>
                      </div>

                      <div className="bg-[#050507] border border-slate-900 p-3.5 rounded-xl flex items-center justify-between gap-4 font-mono text-[8.5px]">
                        <div className="truncate flex-1">
                          <span className="text-[7px] font-mono text-slate-500 block uppercase font-extrabold pb-0.5">SOURCE CITATION MATCH:</span>
                          <span className="text-[#06b6d4] font-bold truncate block">
                            {inquiryResult.searchSourceTitle}
                          </span>
                        </div>
                        <a href={inquiryResult.sourceUrl} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-slate-100/10 hover:bg-slate-100/20 border border-slate-800 rounded text-slate-300 tracking-wider text-[8px] font-mono shrink-0 font-bold block">
                          OPEN SOURCE
                        </a>
                      </div>
                    </div>

                    <div className="border-t border-slate-900 pt-4 mt-4 flex justify-between items-center gap-3">
                      <p className="text-[8px] font-sans text-slate-555 max-w-[50%] leading-relaxed font-semibold">Dynamic training validator ready to map questions based on facts shown above.</p>
                      <button
                        onClick={handleTriggerQuizCompilation}
                        disabled={isCompilingQuiz}
                        className="px-4 py-2.5 border border-[#f59e0b] bg-amber-950/20 hover:bg-amber-950/45 text-[#f59e0b] font-mono text-[8px] font-black uppercase tracking-widest rounded-lg transition-all cursor-pointer flex items-center gap-1.5 leading-none shrink-0 font-bold"
                      >
                        {isCompilingQuiz ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Layers className="w-3.5 h-3.5" />}
                        <span>Compile Custom Quiz</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-[#050507]/45 border border-slate-900 border-dashed rounded-3xl p-6 flex flex-col justify-center items-center text-center space-y-3 min-h-[220px]">
                    <HelpCircle className="w-8 h-8 text-slate-700 stroke-[1.5]" />
                    <p className="text-[8px] font-mono text-slate-600 uppercase tracking-widest max-w-[280px] font-black leading-relaxed">Dispatch a regulatory inquiry on the console to retrieve compliance answers with verified factual Citations.</p>
                  </div>
                )}

                {quizManifest && (
                  <div className="bg-[#07070d]/90 border border-slate-900 rounded-3xl p-6 space-y-4">
                    <div className="flex justify-between items-center border-b border-slate-900 pb-3">
                      <div className="flex items-center gap-1.5">
                        <Terminal className="w-4 h-4 text-cyan-400" />
                        <span className="text-[8.5px] font-mono text-slate-400 font-bold uppercase tracking-widest">Active Verification Exam // {quizManifest.quizId}</span>
                      </div>
                      <span className="text-[7.5px] font-mono bg-indigo-950/40 text-indigo-400 px-2.5 py-1 rounded border border-indigo-900/40 uppercase font-bold">Category {selectedInquiryCat === 'compliance' ? '15' : selectedInquiryCat === 'transport' ? 'Secure Payment Provider' : selectedInquiryCat === 'grants_funding' ? '102' : '7'}</span>
                    </div>

                    {!evaluationReport ? (
                      <div className="space-y-4">
                        {quizManifest.questions.map((q, idx) => (
                          <div key={q.questionId} className="bg-black/60 border border-slate-950 p-4 rounded-2xl space-y-2.5">
                            <p className="text-[9.5px] font-mono font-black text-slate-200 leading-normal">
                              {idx + 1}. {q.questionText}
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1">
                              {q.options.map((opt) => {
                                const isSelected = answeredQuestions[q.questionId] === opt;
                                return (
                                  <button
                                    key={opt}
                                    onClick={() => handleSelectionOption(q.questionId, opt)}
                                    className={`px-3 py-2 text-left rounded-lg font-mono text-[8.5px] border transition-all cursor-pointer truncate ${
                                      isSelected
                                        ? 'border-indigo-500 bg-indigo-950/25 text-indigo-300 font-black'
                                        : 'border-slate-940 bg-slate-950/50 text-slate-400 hover:border-slate-800 hover:text-slate-200'
                                    }`}
                                  >
                                    {opt}
                                  </button>
                                );
                              })}
                            </div>
                            <span className="text-[7.5px] font-mono text-slate-600 block pt-1 leading-normal font-semibold">
                              Hint: {q.explanationHint}
                            </span>
                          </div>
                        ))}

                        <div className="pt-1 flex justify-end">
                          <button
                            onClick={handleEvaluateComplianceQuiz}
                            disabled={Object.keys(answeredQuestions).length < Math.min(3, quizManifest.questions.length)}
                            className="px-5 py-3 bg-[#f59e0b] hover:bg-amber-400 disabled:bg-slate-950 disabled:text-slate-700 text-black font-mono text-[8px] font-black uppercase tracking-widest rounded-xl transition-all cursor-pointer flex items-center gap-1"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            <span>Submit Verification Answers</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <motion.div
                        className="bg-black/60 border border-slate-950 p-5 rounded-2xl space-y-4"
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                      >
                        <div className="flex justify-between items-center bg-[#050508] border border-slate-900 rounded-xl p-4 gap-4">
                          <div>
                            <span className="text-[7px] font-mono text-slate-500 block uppercase font-bold">PERFORMANCE EVALUATION RATING:</span>
                            <span className={`text-[10px] font-mono font-black ${evaluationReport.performanceRating === 'EXCELLENT' ? 'text-emerald-400' : 'text-amber-500 animate-pulse'}`}>
                              {evaluationReport.performanceRating}
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="text-[7px] font-mono text-slate-500 block uppercase font-bold">COMPLIANCE SCORE:</span>
                            <span className="text-[#f59e0b] font-mono font-black text-xs">{evaluationReport.score} / {evaluationReport.totalQuestions} ({Math.round((evaluationReport.score / evaluationReport.totalQuestions) * 100)}%)</span>
                          </div>
                        </div>

                        {evaluationReport.remedialConceptHighlights && evaluationReport.remedialConceptHighlights.length > 0 && (
                          <div className="space-y-1.5 bg-[#030305] border border-slate-950 p-3.5 rounded-xl">
                            <span className="text-[7.5px] font-mono text-indigo-400 font-extrabold uppercase tracking-wide block border-b border-slate-900 pb-1 mb-1.5">Remedial Action Focus Items:</span>
                            <div className="space-y-2">
                              {evaluationReport.remedialConceptHighlights.map((hl) => (
                                <div key={hl} className="flex items-start gap-1.5 text-[8.5px] font-mono text-slate-400 leading-normal font-semibold">
                                  <span className="text-indigo-500 font-black shrink-0 font-bold">•</span>
                                  <span>{hl}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex gap-2 pt-2 border-t border-slate-900">
                          <button
                            onClick={handleRetryQuiz}
                            className="px-3 py-2 bg-slate-955 hover:bg-slate-900 border border-slate-900 text-slate-400 font-mono text-[8px] uppercase tracking-wider rounded-lg transition-all cursor-pointer font-bold"
                          >
                            Retry Quiz
                          </button>
                          <button
                            onClick={handleSyncEvaluationRecord}
                            disabled={isLogSyncing}
                            className={`flex-1 py-2 font-mono text-[8px] uppercase font-black tracking-widest rounded-lg border cursor-pointer transition-all flex items-center justify-center gap-1.5 ${
                              isSyncedReport 
                                ? 'bg-emerald-950/20 border-emerald-800/25 text-emerald-400 font-extrabold' 
                                : 'bg-indigo-600 hover:bg-indigo-500 text-white border-indigo-700'
                            }`}
                          >
                            <Database className="w-3.5 h-3.5" />
                            <span>{isLogSyncing ? 'Mirroring Record...' : isSyncedReport ? '✔ Mirror Complete' : 'Record Score to NexoraX Resource Library'}</span>
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )
        ) : (
          /* High-Fidelity Interactive Sandbox Interface */
          <motion.div 
            key="training-sandbox-node"
            className="flex flex-col space-y-6 relative z-10 flex-1 w-full"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.2 }}
          >
            {/* Control Bar */}
            <div className="flex justify-between items-center bg-[#09090b] border border-slate-800 rounded-2xl p-4 gap-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSandboxActive(false)}
                  className="px-3 py-1.5 border border-slate-700 hover:border-slate-500 hover:bg-slate-900 text-slate-400 hover:text-white rounded-lg font-mono text-[9px] uppercase tracking-wider transition-all flex items-center gap-1.5"
                >
                  ← Close Sandbox
                </button>
                <div className="h-6 w-[1px] bg-slate-800"></div>
                <div>
                  <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">Active Simulator Vector:</div>
                  <div className="text-xs font-sans font-bold text-[#f59e0b] uppercase">{activeSubNode.code} // {activeSubNode.name}</div>
                </div>
              </div>
              
              {/* Step Navigation Tabs */}
              <div className="flex items-center gap-1.5 bg-black p-1 border border-slate-900 rounded-xl">
                <button
                  onClick={() => setSandboxStep('LESSON')}
                  className={`px-3 py-1.5 rounded-lg font-mono text-[8px] uppercase tracking-widest font-bold transition-all ${
                    sandboxStep === 'LESSON' 
                      ? 'bg-indigo-600 text-white' 
                      : 'text-slate-500 hover:text-slate-300'
                  }`}
                >
                  1. Study Material
                </button>
                <button
                  onClick={handleSubmitQuiz} // Show current quiz or init if not done
                  className={`px-3 py-1.5 rounded-lg font-mono text-[8px] uppercase tracking-widest font-bold transition-all ${
                    sandboxStep === 'QUIZ' 
                      ? 'bg-indigo-600 text-white' 
                      : 'text-slate-500 hover:text-slate-300'
                  }`}
                >
                  2. Competency Exam
                </button>
                <button
                  disabled={!quizPass}
                  onClick={() => { if (quizPass) setSandboxStep('CERTIFICATE'); }}
                  className={`px-3 py-1.5 rounded-lg font-mono text-[8px] uppercase tracking-widest font-bold transition-all flex items-center gap-1 ${
                    sandboxStep === 'CERTIFICATE' 
                      ? 'bg-indigo-600 text-white' 
                      : quizPass 
                        ? 'text-indigo-400 hover:text-white cursor-pointer' 
                        : 'text-slate-700 cursor-not-allowed'
                  }`}
                >
                  <Award className="w-3 h-3" />
                  <span>3. Certificate Badge</span>
                </button>
              </div>
            </div>

            {/* Sandbox Step Content */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 flex-1 min-h-[450px]">
              
              {/* Left Sandbox Column (Spans 8) */}
              <div className="lg:col-span-8 flex flex-col space-y-4 bg-zinc-950/80 border border-slate-900 rounded-3xl p-6 relative overflow-hidden justify-between">
                
                {sandboxStep === 'LESSON' && (
                  <div className="space-y-6 flex flex-col justify-between h-full">
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 pb-2 border-b border-slate-900">
                        <BookOpen className="w-5 h-5 text-indigo-400" />
                        <h2 className="text-sm font-mono font-black text-white uppercase tracking-widest">
                          NexoraX operational Manual // Core curriculum
                        </h2>
                      </div>
                      
                      <p className="text-xs text-slate-400 leading-relaxed font-sans">
                        To pass the diagnostic exam, study the parameters of the NexoraX NexoraX Grid below. These regulations are non-negotiable legal realities set by the Office of the Director.
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                        <div className="bg-[#09090b] border border-slate-900 rounded-xl p-4 space-y-2">
                          <h4 className="text-[10px] font-mono font-bold text-[#f59e0b] uppercase tracking-wider flex items-center gap-1.5">
                            <Shield className="w-3.5 h-3.5" />
                            <span>1. Tactical Defense Matrix</span>
                          </h4>
                          <p className="text-[9.5px] font-sans text-slate-400 leading-relaxed">
                            Continuous mirroring runs on <strong className="text-slate-200">CloudSync-02</strong>. Rogue competitor infrastructure operations targeting NexoraX are isolated in <strong className="text-slate-200">India</strong>. Administrative support traffic routes to <strong className="text-slate-200">support@nexoraxdigital.co.uk</strong>.
                          </p>
                        </div>

                        <div className="bg-[#09090b] border border-slate-900 rounded-xl p-4 space-y-2">
                          <h4 className="text-[10px] font-mono font-bold text-[#f59e0b] uppercase tracking-wider flex items-center gap-1.5">
                            <Cpu className="w-3.5 h-3.5" />
                            <span>2. Staffing & Placements</span>
                          </h4>
                          <p className="text-[9.5px] font-sans text-slate-400 leading-relaxed">
                            Director Client Resource Workflow acquisitions for Principal Private Secretary roles (Claire, Helen, Eleanor) are 100% funded under the <strong className="text-slate-200">NexoraX billing records scheme</strong> (NI: <strong className="text-indigo-400">NH******B</strong>).
                          </p>
                        </div>

                        <div className="bg-[#09090b] border border-slate-900 rounded-xl p-4 space-y-2">
                          <h4 className="text-[10px] font-mono font-bold text-[#f59e0b] uppercase tracking-wider flex items-center gap-1.5">
                            <Network className="w-3.5 h-3.5" />
                            <span>3. Infrastructure Coordinates</span>
                          </h4>
                          <p className="text-[9.5px] font-sans text-slate-400 leading-relaxed">
                            Incoming negotiations must be tracked on Planet Telecom Voice Frequency <strong className="text-slate-200">08000 622827</strong>. Standard server container reverse proxies bind exclusively on <strong className="text-slate-200">Port 3000</strong>.
                          </p>
                        </div>

                        <div className="bg-[#09090b] border border-slate-900 rounded-xl p-4 space-y-2">
                          <h4 className="text-[10px] font-mono font-bold text-[#f59e0b] uppercase tracking-wider flex items-center gap-1.5">
                            <Scale className="w-3.5 h-3.5" />
                            <span>4. NexoraX Extraction (Node-07)</span>
                          </h4>
                          <p className="text-[9.5px] font-sans text-slate-400 leading-relaxed">
                            Gross Harvest undergoes a <strong className="text-slate-200">1% 3% Community Allocation</strong> (split equally i.e. 33.33% to Dunfermline foundation, Global and UK charities), then a <strong className="text-slate-200">20% Lifetime 10% Maintenance & Hosting Fee Tax</strong> is extracted on remaining balances. Settled net routed strictly to SecurePayment: <strong className="text-indigo-400">04-**-** | ******15</strong>.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-slate-900 flex justify-end">
                      <button
                        onClick={handleLaunchSandbox}
                        className="px-5 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-mono text-[10px] font-black uppercase tracking-widest rounded-xl transition-all shadow-[0_0_15px_rgba(79,70,229,0.3)] flex items-center gap-2 cursor-pointer animate-pulse"
                      >
                        <span>Initialize Competency Quiz & Accounting Exam</span>
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {sandboxStep === 'QUIZ' && (
                  <div className="space-y-6 overflow-y-auto max-h-[65vh] pr-2">
                    <div className="flex items-center justify-between border-b border-slate-900 pb-3 mb-2">
                      <div className="flex items-center gap-2">
                        <Terminal className="w-5 h-5 text-[#f59e0b]" />
                        <h2 className="text-xs font-mono font-black text-white uppercase tracking-widest">
                          Stage 2: Live Diagnostic Vetting Board
                        </h2>
                      </div>
                      <span className="text-[8.5px] font-mono text-slate-500 uppercase">Solve all sections completely</span>
                    </div>

                    {/* Multiple-choice block */}
                    <div className="space-y-5">
                      <h3 className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest border-l-2 border-[#f59e0b] pl-2">
                        Section A: Operational Strategy Alignment
                      </h3>
                      
                      {quizQuestionsByCategory[activeBlock].map((q, idx) => (
                        <div key={q.id} className="bg-[#09090b] border border-slate-900 rounded-xl p-4 space-y-3">
                          <label className="text-[10.5px] font-mono text-slate-200 block leading-relaxed">
                            <strong>Question {idx + 1}:</strong> {q.question}
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {q.options.map((option, optIdx) => {
                              const isSelected = quizAnswers[q.id] === optIdx;
                              return (
                                <button
                                  key={optIdx}
                                  onClick={() => handleAnswerSelect(q.id, optIdx)}
                                  className={`text-left p-3 rounded-lg text-[9.5px] font-mono transition-all border flex items-center justify-between ${
                                    isSelected 
                                      ? 'bg-indigo-950/40 border-indigo-500 text-white font-bold' 
                                      : 'bg-black/50 border-slate-900 text-slate-400 hover:border-slate-800 hover:text-slate-200'
                                  }`}
                                >
                                  <span>{option}</span>
                                  {isSelected && <Check className="w-3 h-3 text-indigo-400 shrink-0" />}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* accounting challenge block */}
                    <div className="space-y-4 pt-2">
                      <h3 className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest border-l-2 border-[#f59e0b] pl-2">
                        Section B: Practical Node-07 Fiscal Ledger Audit
                      </h3>

                      <div className="bg-[#09090b] border border-slate-900 rounded-2xl p-5 space-y-4">
                        <div className="bg-slate-950/60 border border-slate-900 p-4 rounded-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                          <div>
                            <span className="text-[8px] font-mono text-slate-500 uppercase tracking-wider block font-bold">SAMPLE DAILY GROSS HARVEST:</span>
                            <span className="text-xl md:text-2xl font-mono font-black text-emerald-400 tracking-wider">
                              £{grossHarvestSample.toLocaleString('en-GB')}
                            </span>
                          </div>
                          <button
                            onClick={() => {
                              const calculationOptions = [90000, 150000, 240000, 300000, 450000, 600000];
                              const nextVal = calculationOptions[Math.floor(Math.random() * calculationOptions.length)];
                              setGrossHarvestSample(nextVal);
                            }}
                            className="px-3 py-1.5 border border-slate-800 hover:border-[#f59e0b] text-[8px] font-mono font-bold uppercase text-slate-400 hover:text-white rounded-lg transition-all flex items-center gap-1.5"
                          >
                            <RefreshCw className="w-3 h-3 animate-spin duration-1000" />
                            <span>Cycle Harvest Value</span>
                          </button>
                        </div>

                        <p className="text-[9.5px] font-mono text-slate-400 leading-relaxed uppercase tracking-wider">
                          Compute operational splits under the 1% 3% Community Allocation (with Charity 1 splits) and the 10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee). Enter final decimals rounded to nearest integers or pennies:
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <label className="text-[8.5px] font-mono text-slate-500 uppercase tracking-widest block font-bold">1% 3% Community Allocation Amount (£)</label>
                            <input
                              type="text"
                              placeholder="e.g. 1200"
                              value={calculationInputs.levy}
                              onChange={e => setCalculationInputs(prev => ({ ...prev, levy: e.target.value }))}
                              className="w-full bg-black border border-slate-900 focus:border-indigo-500 rounded-lg p-2.5 font-mono text-xs text-white outline-none"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="text-[8.5px] font-mono text-slate-500 uppercase tracking-widest block font-bold">Charity 1 Split (33.33% of Levy) (£)</label>
                            <input
                              type="text"
                              placeholder="e.g. 400"
                              value={calculationInputs.split}
                              onChange={e => setCalculationInputs(prev => ({ ...prev, split: e.target.value }))}
                              className="w-full bg-black border border-slate-900 focus:border-indigo-500 rounded-lg p-2.5 font-mono text-xs text-white outline-none"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="text-[8.5px] font-mono text-slate-500 uppercase tracking-widest block font-bold">20% 10% Maintenance & Hosting Fee Tax Amount (£)</label>
                            <input
                              type="text"
                              placeholder="e.g. 23760"
                              value={calculationInputs.gavelFee}
                              onChange={e => setCalculationInputs(prev => ({ ...prev, gavelFee: e.target.value }))}
                              className="w-full bg-black border border-slate-900 focus:border-indigo-500 rounded-lg p-2.5 font-mono text-xs text-white outline-none"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="text-[8.5px] font-mono text-slate-500 uppercase tracking-widest block font-bold">Net finance Settlement routed to SecurePayment (£)</label>
                            <input
                              type="text"
                              placeholder="e.g. 95040"
                              value={calculationInputs.net}
                              onChange={e => setCalculationInputs(prev => ({ ...prev, net: e.target.value }))}
                              className="w-full bg-black border border-slate-900 focus:border-indigo-500 rounded-lg p-2.5 font-mono text-xs text-white outline-none"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Validation Errors Overlay */}
                    {quizSubmitted && ((quizScore < quizQuestionsByCategory[activeBlock].length) || calcSuccess === false) && (
                      <div className="bg-red-950/40 border border-red-900/55 p-4 rounded-xl space-y-2">
                        <div className="flex items-center gap-1.5 text-red-400 text-xs font-mono font-bold uppercase tracking-wider">
                          <AlertTriangle className="w-4 h-4 shrink-0 text-red-400 animate-pulse" />
                          <span>Vetting Block: Operational Standards Not Reconciled</span>
                        </div>
                        <ul className="list-disc pl-5 font-mono text-[9px] text-red-300 uppercase space-y-1">
                          {quizScore < quizQuestionsByCategory[activeBlock].length && (
                            <li>Core Vetting: Correct answers ({quizScore} / {quizQuestionsByCategory[activeBlock].length}). Must get 100% correct in Section A.</li>
                          )}
                          {calcErrors.map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Submission Drawer Actions */}
                    <div className="pt-4 border-t border-slate-900 flex justify-between items-center bg-zinc-950/60 p-4 rounded-2xl">
                      <button
                        onClick={() => setSandboxStep('LESSON')}
                        className="px-4 py-2 border border-slate-800 hover:border-slate-600 text-[8.5px] font-mono font-bold text-slate-400 hover:text-white rounded-xl transition-all"
                      >
                        Return to Manual Study
                      </button>
                      
                      <button
                        onClick={handleSubmitQuiz}
                        className="px-6 py-3 bg-emerald-500 hover:bg-emerald-400 text-black font-mono text-[9.5px] font-black uppercase tracking-widest rounded-xl transition-all shadow-[0_0_20px_rgba(16,185,129,0.25)] cursor-pointer"
                      >
                        🔐 Verify Competency Ledger Audit
                      </button>
                    </div>

                  </div>
                )}

                {sandboxStep === 'CERTIFICATE' && (
                  <div className="space-y-6 flex flex-col justify-between h-full">
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 pb-2 border-b border-slate-900">
                        <Award className="w-5 h-5 text-yellow-500" />
                        <h2 className="text-sm font-mono font-black text-white uppercase tracking-widest">
                          NexoraX Competency Seal Authorized
                        </h2>
                      </div>

                      <p className="text-xs text-slate-400 leading-relaxed font-sans">
                        You have validated the strategic standard questions of category <strong className="text-indigo-400 font-bold uppercase">{activeBlock.replace('_', ' ')}</strong> and resolved the Node-07 mathematical auditing ledger with 100% precision! Record your name below to issue the permanent credential.
                      </p>

                      <div className="bg-[#09090b] border border-slate-900 p-4 rounded-xl space-y-2 max-w-md">
                        <label className="text-[8.5px] font-mono text-slate-500 uppercase tracking-widest block font-bold">Personalize Candidate / Trainee Name:</label>
                        <input
                          type="text"
                          value={traineeName}
                          onChange={e => setTraineeName(e.target.value)}
                          placeholder="Candidate Full Name (e.g. Helen, Staff Coordinator)"
                          className="w-full bg-black border border-slate-900 focus:border-indigo-500 rounded-lg p-2.5 font-mono text-xs text-white outline-none"
                        />
                      </div>

                      {/* Display rendered Certificate */}
                      <div id="print-certification-node" className="bg-[#050507] border-2 border-dashed border-amber-500/40 rounded-3xl p-6 md:p-8 relative overflow-hidden flex flex-col space-y-6 select-text shadow-[0_0_40px_rgba(245,158,11,0.06)]">
                        {/* Elegant glowing background rings */}
                        <div className="absolute -top-12 -right-12 w-48 h-48 border border-amber-500/10 rounded-full pointer-events-none animate-spin duration-30000"></div>
                        <div className="absolute -bottom-12 -left-12 w-48 h-48 border border-amber-500/10 rounded-full pointer-events-none animate-spin duration-40000"></div>

                        <div className="flex justify-between items-start border-b border-amber-500/20 pb-4">
                          <div>
                            <span className="text-[9px] font-mono text-[#f59e0b] tracking-widest font-black uppercase">NEXORAX DIGITAL LTD // SC887095</span>
                            <h3 className="text-base font-sans font-black text-white uppercase tracking-wider mt-0.5">NEXORAX ACADEMY CREDENTIAL</h3>
                          </div>
                          <div className="w-10 h-10 border border-amber-500/40 rounded-full flex justify-center items-center text-amber-500 bg-amber-950/20 shadow-[0_0_15px_rgba(245,158,11,0.2)] font-mono text-xs font-black">
                            NX
                          </div>
                        </div>

                        <div className="space-y-4 text-center py-6">
                          <span className="text-[8.5px] font-mono text-slate-500 uppercase tracking-[0.3em] block">THIS SECURE LOG CONTAINS THE VERIFIED COMPETENCY OF:</span>
                          <span className="text-xl md:text-2xl font-serif font-black tracking-wide text-white underline decoration-[#f59e0b] decoration-wavy block py-1">
                            {traineeName || "[TRAINEE NAME INCOMPLETE]"}
                          </span>
                          <p className="text-[10px] font-sans text-slate-400 max-w-lg mx-auto leading-relaxed uppercase tracking-wider">
                            For completing intense multi-stage engineering diagnostics across <strong className="text-white">{activeSubNode.code} ({activeSubNode.name})</strong>. Verified to govern 0.8s SLA loops, process direct SecurePayment routing handshakes, and run continuous active CloudSync-02 mirrors.
                          </p>
                        </div>

                        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 border-t border-slate-900 pt-4 text-left font-mono">
                          <div className="space-y-1">
                            <span className="text-[7.5px] text-slate-500 uppercase font-bold tracking-widest block">AUTHENTICATION DEPUTY SIGNATURE</span>
                            <span className="text-xs font-semibold text-slate-200">Lead Director, Director</span>
                            <span className="text-[7px] text-slate-500 uppercase block">OFFICE OF THE DIRECTOR // NEXORAX ENCLAVE</span>
                          </div>
                          <div className="space-y-1 md:text-right">
                            <span className="text-[7.5px] text-slate-500 uppercase font-bold tracking-widest block">SECURE TRANS_HASH ID</span>
                            <span className="text-[9px] font-bold text-amber-500 block font-mono">{certLogHash}</span>
                            <span className="text-[7px] text-slate-500 uppercase block">GENERATED AT: {new Date().toLocaleDateString('en-GB')} UTC</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-slate-900 flex flex-col md:flex-row justify-between items-center gap-4 bg-zinc-950/60 p-4 rounded-2xl">
                      <button
                        onClick={handleLaunchSandbox}
                        className="px-4 py-2 border border-slate-800 hover:border-slate-500 text-[8.5px] font-mono font-bold text-slate-400 hover:text-white rounded-xl transition-all flex items-center gap-1"
                      >
                        <RotateCcw className="w-3 h-3" />
                        <span>Reset/Retake Evaluation</span>
                      </button>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={handlePrintCertificate}
                          className="px-4 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 text-white font-mono text-[8.5px] uppercase font-bold tracking-widest rounded-xl transition-all flex items-center gap-1.5 cursor-pointer"
                        >
                          <Printer className="w-3.5 h-3.5" />
                          <span>Print Certification PDF</span>
                        </button>
                        
                        <button
                          onClick={handleSaveCertificateToVault}
                          disabled={certifying || vaultSyncStatus === 'success'}
                          className={`px-5 py-2.5 font-mono text-[8.5px] uppercase font-black tracking-widest rounded-xl transition-all flex items-center gap-2 cursor-pointer ${
                            vaultSyncStatus === 'success'
                              ? 'bg-emerald-900/60 border border-emerald-500/30 text-emerald-400'
                              : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-[0_0_15px_rgba(79,70,229,0.3)]'
                          }`}
                        >
                          <Database className="w-3.5 h-3.5" />
                          <span>
                            {vaultSyncStatus === 'syncing' ? 'Mirroring...' : 
                             vaultSyncStatus === 'success' ? '✔ Synced in NexoraX Resource Library' : 
                             vaultSyncStatus === 'error' ? 'Sync Failed - Retry' : 'Record to NexoraX Resource Library'}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

              </div>

              {/* Right Sandbox Column (Spans 4) - Embedded Desk Calculator & Info Terminal */}
              <div className="lg:col-span-4 flex flex-col space-y-4">
                
                {/* Embedded Math Calculator Terminal */}
                <div className="bg-[#050507] border border-slate-900 rounded-3xl p-5 flex flex-col justify-between min-h-[240px] relative">
                  <div>
                    <div className="flex items-center justify-between border-b border-slate-900 pb-2 mb-3">
                      <div className="flex items-center gap-1 text-slate-400 font-mono text-[9px] uppercase font-bold">
                        <Calculator className="w-3.5 h-3.5 text-indigo-400" />
                        <span>L2 Mathematical Auditor Desk</span>
                      </div>
                      <span className="text-[7.5px] font-mono text-slate-500 uppercase">Input formulas directly</span>
                    </div>

                    {/* Calculator display */}
                    <div className="bg-black border border-slate-950 p-2.5 rounded-xl text-right font-mono select-all select-text mb-4">
                      <div className="text-slate-500 text-[10px] tracking-wide truncate h-5">
                        {calcDisplay || '0'}
                      </div>
                      <div className="text-[#f59e0b] font-black text-sm tracking-wider h-6 pt-1">
                        {calcResult ? `= ${calcResult}` : ''}
                      </div>
                    </div>

                    {/* Button Grid */}
                    <div className="grid grid-cols-4 gap-1.5">
                      {['7', '8', '9', '/', '4', '5', '6', '*', '1', '2', '3', '-', '0', '.', '=', '+', 'C'].map((btnValue) => {
                        const isClear = btnValue === 'C';
                        const isEval = btnValue === '=';
                        return (
                          <button
                            key={btnValue}
                            onClick={() => handleCalculatorPress(btnValue)}
                            className={`p-2 font-mono text-center rounded text-[10px] font-bold cursor-pointer transition-all ${
                              isClear 
                                ? 'col-span-4 bg-red-950/20 hover:bg-red-900/40 text-red-400 border border-slate-900' 
                                : isEval
                                  ? 'bg-[#f59e0b] hover:bg-amber-400 text-black font-black'
                                  : 'bg-[#09090b] border border-slate-900 text-slate-300 hover:border-slate-800'
                            }`}
                          >
                            {btnValue}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Trainee Vetting Assistant Info Box */}
                <div className="bg-[#09090b] border border-slate-900 rounded-2xl p-5 space-y-4 flex-1">
                  <div className="text-[9px] font-mono text-indigo-400 uppercase tracking-widest font-bold pb-2 border-b border-slate-900">
                    Live Telemetry Status
                  </div>
                  <div className="space-y-3 font-mono text-[9.5px]">
                    <div className="flex justify-between">
                      <span className="text-slate-500">USER LOGIN:</span>
                      <span className="text-slate-300 truncate tracking-wide max-w-[140px] text-[9.5px]">
                        {auth.currentUser?.email || 'siddique.mahmood@gmail.com'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">SPONSOR LICENSE:</span>
                      <span className="text-slate-300">SC887095</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">MIGRATION TARGETS:</span>
                      <span className="text-slate-300">Staff Coordinator / Communications Associate</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">REVENUE COGNITION:</span>
                      <span className="text-emerald-400">NET SecurePayment ACTIVE</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">CERTIFICATE STATE:</span>
                      <span className={quizPass ? 'text-emerald-400 font-bold' : 'text-amber-500/80 font-bold'}>
                        {quizPass ? 'VETTED' : 'UNVETTED'}
                      </span>
                    </div>
                  </div>

                  <div className="p-3 bg-indigo-950/20 border border-indigo-900/30 rounded-lg space-y-1 mt-4">
                    <span className="text-[8px] font-mono text-indigo-400 uppercase font-black tracking-widest block">Note regarding access:</span>
                    <p className="text-[8.5px] font-sans text-slate-400 leading-relaxed">
                      All certifications generated here are accounted for directly under Director Lead Director's executive guidelines. Passing qualifies administrators for direct production access locks on Node 18.5 parameters.
                    </p>
                  </div>
                </div>

              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};
